import moment from "moment";

import { RecipientDetailsModel } from "@/Appointments/Models/Appointment/RecipientDetailsModel";
import { BilledServiceItem } from "@/Billing/Api/Inbox/Dto/Response/BillingInboxItem";
import { FacturationNetManager } from "@/Billing/Services/FacturationNet/FacturationNetManager";
import { Client } from "@/Common/Api/Client";
import { GenericResponse } from "@/Common/Api/Dto/GenericResponse";
import { ExternalBillingClaimDto } from "@/Common/Api/ExternalBilling/Dto/ExternalBillingClaimDto";
import { PrivateBillCostCenter } from "@/Common/Components/Billing/PrivateBillCostCenter";
import { TimerComponentModel } from "@/Common/Components/Inputs/TimerComponent";
import { ArrayHelper } from "@/Common/Helpers/ArrayHelper";
import { BillingPaymentMethodsHelper } from "@/Common/Helpers/BillingPaymentMethodsHelper";
import { CommonHelper } from "@/Common/Helpers/CommonHelper";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { PrintHelper } from "@/Common/Helpers/PrintHelper";
import { PatientAppointmentListItem } from "@/Common/Models/Appointments/PatientAppointmentListItem";
import { MyleObjectType } from "@/Common/Models/Enum/MyleObjectType";
import { Guid } from "@/Common/Models/Guid";
import { MedicalRequestType } from "@/Common/Models/MedicalRequest/Enum/MedicalRequestType";
import { MyleTabKey } from "@/Common/Models/MyleMenu/enums/MyleTabKey";
import { PatientTab } from "@/Common/Models/Patient/PatientEnums";
import { Person } from "@/Common/Models/Person/Person";
import { SharedItem } from "@/Common/Models/Portal/SharedItem/SharedItem";
import { SecondScreenNoteData } from "@/Common/Models/SecondScreenNoteData";
import { SignedPrintAuthorization } from "@/Common/Models/SignedPrintAuthorization";
import { SiteUIModel } from "@/Common/Models/Sites/SiteUIModel";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { integrationBus } from "@/Common/Modularity/IntegrationBus/IntegrationBus";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import * as SubServerOperations from "@/Common/Operations/operations.server.sub";
import { CanadaState } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { BillingProvider, BillingProviderHelper } from "@/Common/Services/ExternalBilling/BillingProvider";
import { SecondScreenNoteService } from "@/Common/Services/SecondScreenNoteService";
import { BaseAction } from "@/Common/Stores/Actions/BaseAction";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { ServerEvent, ServerEventWithRequest, ServerEventWithThen } from "@/Common/Stores/Actions/ServerEvent";
import { Dispatcher } from "@/Common/Stores/Dispatcher";
import { BaseServerOperationHandler } from "@/Common/Stores/Handlers/Server/BaseServerOperationHandler";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { FlowsheetConstants } from "@/Flowsheets/FlowsheetConstants";
import { FreeFormImage } from "@/Patient/Api/Dto/Responses/FreeFormImage";
import { MedicalNoteModel } from "@/Patient/Api/Dto/Responses/MedicalNoteModel";
import { PatientFormAPSS } from "@/Patient/Api/Dto/Responses/PatientFormAPSS";
import { FacturationDTO, MedfarBillLightPreview } from "@/Patient/Api/MedfarBilling/Dto/Responses/FacturationReponse";
import { LightPrivateBill } from "@/Patient/Api/PrivateBill/Dto/LightPrivateBill";
import { WorkSafeBcFormLight } from "@/Patient/Components/MedicalNote/WorkSafeBc/Models/WorkSafeBcFormLight";
import { MedicalNoteHelper } from "@/Patient/Helpers/MedicalNoteHelper";
import { EncounterSearchIndexRebuildMode, PatientMedicalSummaryHelper } from "@/Patient/Helpers/PatientMedicalSummaryHelper";
import { PatientModelHelper } from "@/Patient/Helpers/PatientModelHelper";
import { EncounterLoadedEvent, PortalSyncSuccessEvent } from "@/Patient/IntegrationBus/PatientEvents";
import { FormDisplay } from "@/Patient/Models/Forms/FormDisplay";
import { PatientForms } from "@/Patient/Models/Forms/RadPdf/Forms";
import { PatientSelectedBillInfoResponse } from "@/Patient/Models/History/PatientPrivateBillItem";
import { Encounter } from "@/Patient/Models/MedicalSummary/Encounter";
import { EncounterNote } from "@/Patient/Models/MedicalSummary/EncounterNote";
import { BaseObstetricalFileModel } from "@/Patient/Models/ObstetricalFile/BaseObstetricalFileModel";
import { ObstetricalFileModel } from "@/Patient/Models/ObstetricalFile/ObstetricalFileModel";
import { PdfFormInboxItem } from "@/Patient/Models/ShareItems/PdfFormInboxItem";
import { BillableServiceDropdownItem } from "@/Patient/Models/UI/BillingHistory/BillableServiceDropdownItem";
import { IBillableService } from "@/Patient/Models/UI/History/IBillableService";
import { MedicalNoteViewMode } from "@/Patient/Models/UI/MedicalNote/Enums/MedicalNote";
import { IPatientMedicalNoteModel } from "@/Patient/Models/UI/MedicalNote/IPatientMedicalNoteModel";
import { IPatientMedicalSummaryModel } from "@/Patient/Models/UI/MedicalSummary/IPatientMedicalSummaryModel";
import { AppointmentStatusChangeEventType, IPrintConsultationRequest, IPrintLabRequest } from "@/Patient/Stores/Actions/PatientMedicalNoteActions";
import { PatientMedicalNoteHandler } from "@/Patient/Stores/Handlers/Local/MedicalNote/PatientMedicalNoteHandler";
import { PatientStore } from "@/Patient/Stores/PatientStore";
import { ConsultationRequestsInboxItem, RequestsInboxItem } from "@/Portfolio/Models/MyNotesInboxItem";

class PatientMedicalNoteResponseHandler extends BaseServerOperationHandler {
    constructor(action: BaseAction) {
        super(action);

        this.RegisterOperation(ServerOperations.Server.Myle_Patient_MedicalNote_LoadEncounter, this.LoadEncounterEdit, SubServerOperations.MedicalNote.LoadEncounterForEdit);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_MedicalNote_LoadEncounter, this.LoadEncounterEditAndRedirectToPatientTab, SubServerOperations.MedicalNote.LoadEncounterForEditAndRedirectToPatientTab);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_MedicalNote_LoadEncounter, this.LoadEncounterView, SubServerOperations.MedicalNote.LoadEncounterForView);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_MedicalNote_LoadEncounter, this.LoadEncounterRefresh, SubServerOperations.MedicalNote.ForSummaryRefresh);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_MedicalNote_LoadEncounter, this.LoadEncounterWithoutOpeningView, SubServerOperations.MedicalNote.ForGrowthChart);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_MedicalNote_LoadEncountersById, this.preLoadEncountersCache);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_MedicalNote_AddEncounterNote, this.AddEncounterNote);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_History_GetSelectedBillInformation, this.LoadPrivateBill, SubServerOperations.PrivateBill.LoadInfoFromNote);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_History_GetSelectedBillInformation, this.LoadPrivateBill_ForPrint, SubServerOperations.PrivateBill.LoadInfoFromNote_ForPrint);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_MedicalNote_CreateEncounter, this.CreateEncounter);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_MedicalNote_AddLabRequest, this.AddLabRequest);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_MedicalNote_UpdateConsultationDetails, this.updateConsultationDetails);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_MedicalNote_CreateNewForm, this.CreateNewForm, null, false);
        this.RegisterOperation(ServerOperations.Server.Myle_SaveHandNoteFile, this.SaveHandNoteFile);
        this.RegisterOperation(ServerOperations.Server.Myle_DeleteHandNoteFile, this.DeleteHandNoteFile);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_MedicalNote_DeleteForm, this.onDeletePdfForm);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_MedicalNote_IsPersistant, this.onPatientFormIsPersistant);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_MedicalNote_GetStatus, this.GetEncounterStatus);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_MedicalNote_SaveEncounter, this.SaveEncounter);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_MedicalNote_SaveAsNewEncounter, this.SaveAsNewEncounter);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_MedicalNote_SignoffEncounter, this.SignoffEncounter);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_GeneratePrescriptionPDF, this.PrintPrescription);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_MedicalNote_GenerateNotePDF, this.PrintMedicalNote);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_History_SavePrivateBill, this.FinishPrivateBillSave);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_History_GeneratePatientPrivateBillPDF, this.GeneratePatientPrivateBillPDF, SubServerOperations.MedicalNote.GeneratePrivateBillForPrint);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_History_GeneratePatientPrivateBillPDF, this.GeneratePrivateBillForReuse, SubServerOperations.MedicalNote.GeneratePrivateBillForReuse);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_History_InvalidatePrivateBill, this.CompleteInvalidationBill);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_History_PrivateBill_LoadCostCenterForMexico, this.LoadCostCenter);
        this.RegisterOperation(ServerOperations.Server.MedfarBilling_GetBill, this.LoadMedfarBill);
        this.RegisterOperation(ServerOperations.Server.MedfarBilling_DeleteBill, this.DeleteMedfarBill);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_DeletePendingPrescription, this.DeletePendingPrescription);
        this.RegisterOperation(ServerOperations.Server.Myle_Resident_SendPendingApproval, this.SendPendingApproval);
        this.RegisterOperation(ServerOperations.Server.Myle_Resident_LoadPendingStatus, this.LoadPendingStatus);
        this.RegisterOperation(ServerOperations.Server.Myle_PrintConsultationRequest, this.PrintConsultationRequest);
        this.RegisterOperation(ServerOperations.Server.Myle_Faxing_SendConsultationRequest, this.FaxConsultationRequest, SubServerOperations.MedicalNote.FaxConsultationRequest);
        this.RegisterOperation(ServerOperations.Server.Myle_UploadApssAttachments, this.Myle_UploadApssAttachments);
        this.RegisterOperation(ServerOperations.Server.Myle_GetPatientForms, this.Myle_GetPatientForms);
        this.RegisterOperation(ServerOperations.Server.Myle_PrivateBill_LoadLightPrivateBill, this.LoadLightPrivateBill, SubServerOperations.PrivateBill.LoadListForReuse);
        this.RegisterOperation(ServerOperations.Server.Myle_PrivateBill_LoadLightPrivateBill, this.LoadListPrivateBillForEncounter, SubServerOperations.PrivateBill.LoadListForEncounter);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_History_GetSelectedBillInformation, this.LoadPrivateBillInfoForReuse, SubServerOperations.PrivateBill.LoadInfoFromReusing);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_History_GetSelectedBillInformation, this.LoadPrivateBillInfoForEdit, SubServerOperations.PrivateBill.LoadInfoFromEdit);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_Merged_GrowthCurvesAndVitalsPDF, this.printCharts);
        this.RegisterOperation(ServerOperations.Server.Myle_PrintSelectedRequest, this.PrintSelectedRequest);
        this.RegisterOperation(ServerOperations.Server.Myle_Faxing_SendLabRequest, this.faxLabRequest);
        this.RegisterOperation(ServerOperations.Server.Myle_UnapproveMedicalEncounter, this.UnapproveMedicalEncounter);
        this.RegisterOperation(ServerOperations.Server.Myle_DeleteMedicalEncounter, this.DeleteMedicalEncounter);
        this.RegisterOperation(ServerOperations.Server.Myle_LoadAttachedCommunications, this.loadAttachedCommunications, SubServerOperations.AttachedCommunications.Encounter);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_Portal_SyncSharedItem, this.finishRequestsPortalSync, SubServerOperations.PortalLinkedItemStatus.Requests);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_Portal_SyncSharedItem, this.finishPortalSync, SubServerOperations.PortalLinkedItemStatus.MedicalNoteForm);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_Portal_RevokeSharedItem, this.finishPortalSync, SubServerOperations.PortalRevokeItem.FromMedicalNote);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_MedicalNote_ReloadForms, this.reloadForms);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_Portal_GetSharedItem, this.reloadSharedItem);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_MedicalNote_NewObstetricalFile, this.handleOnObstetricalContributionCreated);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_MedicalNote_ObstetricalFileContribution, this.handleOnObstetricalContributionCreated);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_MedicalNote_DeleteObstetricalFile, this.deleteObstetricalFile);
        this.RegisterOperation(ServerOperations.Server.WorkSafeBc_GetExistingFormList, this.loadWorkSafeBcForms);
        this.RegisterOperation(ServerOperations.Server.Myle_GetSignedPrintAuthorization, this.loadSignedPrintAuthorizations);
        this.RegisterOperation(ServerOperations.Server.Myle_Faxing_GetSentFaxesByItemIds, this.loadSentFaxesByItemIds);
    }

    private Myle_GetPatientForms(response: ServerEvent, store: PatientStore): void {
        var ApssForms: { [encounterId: string]: PatientFormAPSS[] } = response.Data.patientFormsAPSS;

        let patientModel = store.PatientMedicalNoteModels[response.PatientId];

        var noteModel = store.PatientMedicalNoteModels[response.PatientId];
        var noteModel = noteModel.set("IsCreatingAPSSForm", false);
        var noteModel = noteModel.set("PatientAPSSForms", ApssForms[patientModel.get("Encounter").Id].toImmutable());
        store.PatientMedicalNoteModels[response.PatientId] = noteModel;
    }

    private Myle_UploadApssAttachments(response: ServerEvent): void {
        var formId = response.Data.ApssRequestId;

        window.setTimeout(() => {
            var op = new LocalEvent(LocalPatientOperations.MedicalNote.LoadApssFormInfo);
            op.AddItem("formId", formId);
            LocalEventDispatcher.dispatch(op);
        }, 50);
    }

    private DeletePendingPrescription(response: ServerEvent, store: PatientStore): void {
        let errors = response.Errors;

        // handle DSQ error
        if (errors.length > 0)
            sessionStore.showPopup(errors);

        store.ActionsMedicationTab.ReloadMedications(response.PatientId);
    }

    private DeleteMedfarBill(response: ServerEvent, store: PatientStore): void {
        if (response.Data.Success) {
            let patientModel = store.PatientMedicalNoteModels[response.PatientId];

            store.ActionsMedicalNote.LoadMedfarBillForEncounter(patientModel.get("Encounter").Id);
        }
    }

    private LoadMedfarBill(response: ServerEvent, store: PatientStore): void {
        const rawBills: FacturationDTO[] = response.Data.MedfarBillingList ?? [];

        if (response.Data.Has500Patients != null)
            sessionStore.has500patient = response.Data.Has500Patients;

        let patientModel = store.PatientMedicalNoteModels[response.PatientId];
        patientModel = patientModel.set("EncounterMedfarBills", rawBills);
        patientModel = patientModel.set("EncounterMedfarBillsLight", this.getMedfarBillLightPreviews(rawBills));

        store.PatientMedicalNoteModels[response.PatientId] = patientModel;
    }

    private getMedfarBillLightPreviews(rawBills: FacturationDTO[]): MedfarBillLightPreview[] {
        const medfarLightBillsList: MedfarBillLightPreview[] = [];

        for (let rawBill of rawBills) {
            const lightBill = new MedfarBillLightPreview;

            lightBill.FacturationRequestId = rawBill.FacturationRequestID;
            lightBill.CreationDate = rawBill.CreationDate;
            lightBill.IsEditable = false;
            lightBill.BatchId = rawBill.BatchId;
            lightBill.BillDate = rawBill.DateFacturation;

            if (rawBill.FacturationCodes && rawBill.FacturationCodes.length > 0) {
                lightBill.BillSummary = "( ";
                for (let s = 0; s < rawBill.FacturationCodes.length; s++) {
                    if (s === rawBill.FacturationCodes.length - 1)
                        lightBill.BillSummary += rawBill.FacturationCodes[s].CodeFacturation;
                    else
                        lightBill.BillSummary += rawBill.FacturationCodes[s].CodeFacturation + ", ";
                }

                lightBill.BillSummary += " )";
            }

            medfarLightBillsList.push(lightBill);
        }

        return medfarLightBillsList;
    }

    private LoadEncounterEdit(response: ServerEventWithThen<any, () => void>, store: PatientStore): void {
        this.LoadEncounter(response, store, MedicalNoteViewMode.Edit);
    }

    private LoadEncounterEditAndRedirectToPatientTab(response: ServerEventWithThen<any, () => void>, store: PatientStore): void {
        this.LoadEncounter(response, store, MedicalNoteViewMode.Edit, true, true);
    }

    private LoadEncounterView(response: ServerEventWithThen<any, () => void>, store: PatientStore): void {
        this.LoadEncounter(response, store, MedicalNoteViewMode.View);
    }

    private LoadEncounterWithoutOpeningView(response: ServerEventWithThen<any, () => void>, store: PatientStore): void {
        this.LoadEncounter(response, store, MedicalNoteViewMode.View, false);
    }

    private LoadEncounter(
        response: ServerEventWithThen<any, () => void>,
        store: PatientStore,
        edit: MedicalNoteViewMode,
        openView: boolean = true,
        redirectToPatientTab: boolean = false
    ): void {
        const responseData: MedicalNoteModel = response.Data;
        let summaryModel = store.PatientMedicalSummaryModels[response.PatientId];

        let responseModel = this.getImmutableNoteModelFromResponse(responseData, summaryModel);

        let encounters = summaryModel?.get("Encounters").toArray();
        if (!responseData.Encounter.IsSignedOff && cacheStore.ClinicSettings.EnableAutoLinkAppointmentToNote) {
            const medicalNote = encounters?.filter(x => x.Id === responseData.Encounter.Id).firstOrDefault();
            const appointmentId = responseData.Encounter.AppointmentId ?? medicalNote?.SuggestedAppointmentLinkId;
            const enableAppointmentLinkSuggestions = responseData.Encounter.EnableAppointmentLinkSuggestions;
            let encounter = responseModel.get("Encounter");
            if (enableAppointmentLinkSuggestions && !appointmentId) {
                this.GetLinkedAppointmentId(responseData.PatientContactId, responseData.Encounter, store).then(id => {
                    encounter.SuggestedAppointmentLinkId = id;
                    responseModel.set("Encounter", encounter);
                });
            }

            if (medicalNote?.SuggestedAppointmentLinkId) {
                encounter.SuggestedAppointmentLinkId = medicalNote.SuggestedAppointmentLinkId;
                responseModel.set("Encounter", encounter);
            }
        }

        // Fallback to View mode if encounter is signed
        if (responseData.Encounter.SignedOffDate != null)
            edit = MedicalNoteViewMode.View;

        if (edit === MedicalNoteViewMode.Edit && responseData.Encounter.Id != store.PatientUIModels[response.PatientId].get("EditEncounterIdContext")) {
            // Is New Note
            responseModel = responseModel.set("LeftMenuOpen", false);
        }
        // update encounter in summary
        if (summaryModel != null) {
            let displayedEncounters = summaryModel.get("DisplayedEncounters").toArray();
            encounters = encounters.filter(x => x.Id != responseData.Encounter.Id);
            encounters.push(responseData.Encounter);
            encounters = ArrayHelper.SortDynamic(encounters, "ConsultationDate", true, false, true);
            displayedEncounters = displayedEncounters.filter(x => x.Id != responseData.Encounter.Id);
            displayedEncounters.push(responseData.Encounter);
            displayedEncounters = ArrayHelper.SortDynamic(displayedEncounters, "ConsultationDate", true, false, true);

            summaryModel = summaryModel.set("DisplayedEncounters", Immutable.List(displayedEncounters));
            summaryModel = summaryModel.set("Encounters", Immutable.List(encounters));
            store.PatientMedicalSummaryModels[response.PatientId] = summaryModel;
        }

        if (edit == MedicalNoteViewMode.Edit) {
            let undoModel = responseModel.set("ViewMode", responseModel.get("ViewMode")); // just to get clone for undo model.get("ViewMode")) //just to get clone for undo model.set("ViewMode", noteModel.get("ViewMode")) //just to get clone for undo model
            store.PatientMedicalNoteUndoModels[response.PatientId] = undoModel;
        }

        let responsePatientId = responseData.PatientContactId;
        if (responsePatientId && responsePatientId.length > 0) {
            responseModel = responseModel.set("ViewMode", edit);
            if (edit == MedicalNoteViewMode.Edit) {
                store.PatientUIModels[response.PatientId] = store.PatientUIModels[response.PatientId].set("EditEncounterIdContext", responseModel.get("Encounter").Id);
                sessionStore.setEncounterTimer(new TimerComponentModel(response.PatientId, responseModel.get("Encounter").Id));
            }

            patientStore.PatientMedicalNoteModels[responsePatientId] = responseModel;

            // switch view to medicalNote
            let uiModel = patientStore.PatientUIModels[responsePatientId];

            if (redirectToPatientTab) {
                cacheStore.UpdateTab(MyleTabKey.Patients);
                patientStore.showPatientTab();
            }

            if (openView)
                uiModel = uiModel.set("ActiveVerticalTab", PatientTab.MedicalNote);

            patientStore.PatientUIModels[responsePatientId] = uiModel;
        }

        store.IsLoading = false;

        if (sessionStore.getCurrentEmployee().isActiveSiteMexico() === false) {
            store.ActionsMedicalNote.LoadMedfarBillForEncounter(responseData.Encounter.Id);
        }

        store.ActionsMedicalNote.LoadSignedAuthorizations({
            prescriptonIds: responseModel.get("Prescriptions").toArray().map(p => p.PrescriptionId),
            requestIds: responseModel.get("Requests").toArray().map(p => p.RequestId),
            consultationRequestIds: responseModel.get("ConsultationRequests").toArray().map(p => p.ConsultationRequestId),
            patientFormIds: responseModel.get("PatientForms").toArray().map(p => p.Id),
            encounterId: responseData.Encounter.Id
        });

        store.ActionsMedicalNote.LoadPendingApprovalStatus(responseData.Encounter.Id);
        store.ActionsMedicalSummary.LoadPatientPendingItems(responseData.PatientContactId);
        if (edit == MedicalNoteViewMode.Edit) { // Manage encounter patient related appointment states
            store.ActionsMedicalNote.ManagePatientAppointmentState(
                responseData.PatientContactId,
                responseData.Encounter.Id,
                responseData.Encounter.ConsultationDate,
                AppointmentStatusChangeEventType.ModifyEncounter
            );
        }

        if (response.then != null)
            response.then();
        this.updateInvoiceStatuses(response, store);

        integrationBus.emit(new EncounterLoadedEvent({ encounterId: responseData.Encounter.Id, patientContactId: responseData.PatientContactId as Guid }));

        const encounterSite = cacheStore.GetSiteById(responseData.Encounter.SiteId);
        if (cacheStore.ClinicSettings.EnableWorkSafeBcFormFeature && encounterSite?.isSiteInProvince(CanadaState.BritishColumbia)) {
            store.ActionsMedicalNote.loadWorkSafeBcForms(responseData.Encounter.Id, response.PatientId);
        }

        if (cacheStore.ClinicSettings.EnableChronicDiseaseManagement) {
            store.FlowsheetActions.loadFlowsheets(responseData.Encounter.Id, response.PatientId);
        }

        // get sent faxes related to encounter
        if (cacheStore.ClinicSettings.EnableFaxSendingIndicator) {
            const sentFaxItemIdsToGet: string[] = [
                responseData.Encounter.Id,
                ...responseData.Requests?.map(p => p.RequestId) ?? [],
                ...responseData.ConsultationRequests?.map(p => p.ConsultationRequestId) ?? [],
                ...responseData.PatientForms?.map(p => p.Id) ?? [],
                ...responseData.PatientForms?.flatMap(p => p.SignedPrintAuthorizations?.map(auth => auth.AttachmentId) || []).filter(Boolean) ?? [],
                ...responseModel.get("Prescriptions")?.toArray().map(p => p.PrescriptionId) ?? []
            ];

            if (responseData.ObstetricalFileModel) {
                sentFaxItemIdsToGet.push(responseData.ObstetricalFileModel.ContributionId);
            }

            store.ActionsMedicalNote.loadSentFaxes(sentFaxItemIdsToGet);
        }
    }

    private getImmutableNoteModelFromResponse(responseData: MedicalNoteModel, summaryModel: IPatientMedicalSummaryModel): IPatientMedicalNoteModel {
        let responseModel: IPatientMedicalNoteModel = Immutable.fromJS(PatientModelHelper.getDefaultPatientMedicalNoteModel());
        let responseModelData: IPatientMedicalNoteModel = Immutable.fromJS(responseData);

        responseModel = responseModel.merge(responseModelData); // here we overwrite default values coming from helper with values coming from response

        // deal with immutable.js converting nested objects into immutable - we do not want that
        responseModel = responseModel
            .set("Encounter", responseData.Encounter)
            .set("EncounterNotes", Immutable.List(responseData.EncounterNotes))
            .set("Requests", Immutable.List(responseData.Requests.map(r => new RequestsInboxItem(r))))
            .set("ConsultationRequests", Immutable.List(responseData.ConsultationRequests.map(cr => new ConsultationRequestsInboxItem(cr))))
            .set("PrivateBills", Immutable.List(responseData.PrivateBills))
            .set("PatientAPSSForms", Immutable.List(responseData.PatientAPSSForms))
            .set("EncounterIsDeletable", responseData.EncounterIsDeletable)
            .set("EncounterUnapprouvability", responseData.EncounterUnapprouvability)
            .set("FacturationNetBills", Immutable.List(responseData.FacturationNetBills))
            .set("ExternalBillingClaims", Immutable.List(responseData.ExternalBillingClaims))
            .set("HasPreviousObstetricalPdfForms", responseData.HasPreviousObstetricalPdfForms)
            .set("HasObstetricalFilesInOtherEncounters", responseData.HasObstetricalFilesInOtherEncounters)
            .set("ObstetricalFileFormDisplay", FormDisplay.prepareForObstetricalFile(responseData.ObstetricalFileModel))
            .set("ObstetricalFileModel", responseData.ObstetricalFileModel ? new ObstetricalFileModel(responseData.ObstetricalFileModel) : null)
            .set("PreviousObstetricalFiles", responseData.PreviousObstetricalFiles?.map(p => new ObstetricalFileModel(p)) ?? []);

        if (responseData.Prescriptions)
            responseModel = responseModel.set("Prescriptions", Immutable.List(responseData.Prescriptions));
        else {
            const prescriptionItems = summaryModel.get("Prescriptions");
            responseModel = responseModel.set("Prescriptions", prescriptionItems.toArray().filter(x => x.EncounterId == responseData.Encounter.Id).toImmutable());
        }

        const patientForms = responseData.PatientForms;
        patientForms.forEach(f => { f.contextOpen = false; }); // new property for front end use
        responseModel = responseModel.set("PatientForms", Immutable.List(patientForms.map(form => PdfFormInboxItem.fromPatientForms(form, responseData.Encounter.Id)))); // preload the actual list
        responseModel = responseModel.set("HandwrittenNotes", Immutable.List(responseData.HandwrittenNotes));

        // clear off any previous concurent note edition model values
        responseModel = responseModel.set("ConcurrentEditSignedOffBy", "")
            .set("ConcurrentEditSignedOffDate", null)
            .set("ConcurrentEditShowWarningPopup", false);

        responseModel = responseModel
            .set("IndividualTasksLinkedItems", responseData.IndividualTaskLinkedItems)
            .set("AreAllTasksCompleted", responseData.AreAllTasksCompleted)
            .set("AreAllMessagesRead", responseData.AreAllMessagesRead)
            .set("IndividualMessageLinkedItems", responseData.IndividualMessageLinkedItems)
            .set("IndividualRecallLinkedItems", responseData.IndividualRecallLinkedItems)
            .set("AreAllRecallsCompleted", responseData.AreAllRecallsCompleted)
            .set("WorkSafeBcForms", [])
            .set("SentFaxes", {}); // SentFaxes will be loaded after the note, as this is a resource-intensive request

        // update encounter in summary
        if (summaryModel) {
            const onGoing = summaryModel.get("MedicalItemsProblemsOnGoing");
            const history = summaryModel.get("MedicalItemsProblemsHistory");
            const surgeryHistory = summaryModel.get("MedicalItemsSurgeryHistory");
            const psychiatricHistory = summaryModel.get("MedicalItemsPsychiatricHistory");
            const gynecologicalHistory = summaryModel.get("MedicalItemsGynecologicalHistory");
            const concat = onGoing.concat(history, surgeryHistory, psychiatricHistory, gynecologicalHistory);

            responseModel = responseModel.set("MedicalItemsProblemsAndDiagnostics", Immutable.List(concat));
        }

        return responseModel;
    }

    private preLoadEncountersCache(response: ServerEventWithThen<any, () => void>, store: PatientStore): void {
        const responseData: MedicalNoteModel[] = response.Data.Encounters;
        const summaryModel = store.PatientMedicalSummaryModels[store.getCurrentPatientContactId()];
        let noteModels = store.PatientMedicalNotesCache[response.PatientId] ?? [];

        responseData.forEach(medicalNoteModel => {
            const currentNoteIndex = noteModels.findIndex(note => note.get("Encounter").Id === medicalNoteModel?.Encounter?.Id);
            const encounterSite = cacheStore.GetSiteById(medicalNoteModel?.Encounter?.SiteId);

            if (currentNoteIndex !== -1) {
                noteModels.splice(currentNoteIndex, 1);
            }

            const rawBills: FacturationDTO[] = response?.Data?.MedfarBills?.MedfarBillingList ?? [];
            const externalBills: ExternalBillingClaimDto[] = response?.Data?.ExternalBills?.claims ?? [];
            let noteModel = this.getImmutableNoteModelFromResponse(medicalNoteModel, summaryModel);

            noteModel = noteModel.set("EncounterMedfarBills", rawBills)
                .set("EncounterMedfarBillsLight", this.getMedfarBillLightPreviews(rawBills))
                .set("ExternalBillingClaims", externalBills.toImmutable());

            if (cacheStore.ClinicSettings.EnableWorkSafeBcFormFeature && encounterSite?.isSiteInProvince(CanadaState.BritishColumbia)) {
                const workSafeBcForms: WorkSafeBcFormLight[] = response?.Data?.WorkSafeBcForms?.Forms?.filter(
                    (p: WorkSafeBcFormLight) => p.EncounterId === medicalNoteModel?.Encounter?.Id
                )?.map(p => new WorkSafeBcFormLight(p)) ?? [];

                noteModel = noteModel.set("WorkSafeBcForms", workSafeBcForms);
            }

            noteModels.push(noteModel);
        });

        noteModels = noteModels.sortByPropertyDesc(note => new Date(note.get("Encounter").ConsultationDate).getTime());
        store.PatientMedicalNotesCache[response.PatientId] = noteModels;
    }

    private LoadEncounterRefresh(response: ServerEvent, store: PatientStore): void {
        // set model
        let responseData: MedicalNoteModel = response.Data;
        const patientContactId: string = responseData.PatientContactId;

        // update encounter in summary
        let summaryModel = store.PatientMedicalSummaryModels[patientContactId];

        if (summaryModel) {
            let encounters = summaryModel.get("Encounters").toArray();
            let displayedEncounters = summaryModel.get("DisplayedEncounters").toArray();
            encounters = encounters.filter(x => x.Id != responseData.Encounter.Id);
            encounters.push(responseData.Encounter);
            encounters = ArrayHelper.SortDynamic(encounters, "ConsultationDate", true);
            displayedEncounters = displayedEncounters.filter(x => x.Id != responseData.Encounter.Id);
            displayedEncounters.push(responseData.Encounter);
            displayedEncounters = ArrayHelper.SortDynamic(displayedEncounters, "ConsultationDate", true);
            summaryModel = summaryModel.set("DisplayedEncounters", Immutable.List(displayedEncounters));
            summaryModel = summaryModel.set("Encounters", Immutable.List(encounters));
            store.PatientMedicalSummaryModels[patientContactId] = summaryModel;
            const encounterId: Guid = response.Data.Encounter.Id;
            let encounterNotes: EncounterNote[] = response.Data.EncounterNotes;
            let noteModel = store.PatientMedicalNoteModels[patientContactId];
            if (noteModel != null) {
                let encounter = noteModel.get("Encounter");
                if (!(encounter == null) && encounter.Id == encounterId) {
                    noteModel = noteModel.set("EncounterNotes", Immutable.List(encounterNotes));
                }
                noteModel = noteModel.set("IsNotePopupOpen", false);
            }
            store.PatientMedicalNoteModels[patientContactId] = noteModel;

            // ensure we display current employee as contributor to current note
            store.updateContributorsEncountersData(GuidHelper.AsGuid(patientContactId), encounterId);
        }

        sessionStore.hideGlobalOverlay();
    }

    private AddEncounterNote(response: ServerEvent, store: PatientStore): void {
        let patientContactId = response.Data.EncounterPatientContactId;
        let encounterNotes: EncounterNote[] = response.Data.EncounterNotes;

        let noteModel = store.PatientMedicalNoteModels[patientContactId];
        if (noteModel != null) {
            let encounter = noteModel.get("Encounter");
            if (encounter != null) {
                noteModel = noteModel.set("EncounterNotes", Immutable.List(encounterNotes));
            }

            noteModel = noteModel.set("IsNotePopupOpen", false);
        }

        store.PatientMedicalNoteModels[patientContactId] = noteModel;
        sessionStore.hideGlobalOverlay();
    }

    private LoadPrivateBill(response: ServerEvent, store: PatientStore): void {
        // todo: this is a code smell  - works thanks to var. Chage this to let scope variable (safer) and it breaks.
        let privateBillInfo = response.Data.PrivateBillInfo;
        let billedService = response.Data.BilledService;
        var noteModel = null;

        if (!(privateBillInfo == null)) {
            noteModel = patientStore.PatientMedicalNoteModels[privateBillInfo.PatientContactId];

            if (!(noteModel == null)) {
                var privateBillModel = noteModel.get("PrivateBillPopupModel");
                let billInfo = response.Data.PrivateBillInfo;

                let dateVisit = moment(billInfo.VisitDate).format("YYYY/MM/DD");
                let timeVisit = moment(billInfo.VisitDate).format("HH:mm:ss");
                privateBillModel = privateBillModel.set("BillDate", dateVisit + " " + cacheStore.GetString("AtLabel") + " " + timeVisit);
                privateBillModel = privateBillModel.set("DateOfVisit", dateVisit);

                let site = cacheStore.GetSiteById(billInfo.SiteId);
                privateBillModel = privateBillModel.set("SiteName", site.Model.ClinicalName);
                privateBillModel = privateBillModel.set("SelectedBillSiteId", billInfo.SiteId);
                privateBillModel = privateBillModel.set("IsNewBill", false);
                privateBillModel = privateBillModel.set("Notes", billInfo.Notes);

                const paymentMethodLabel = BillingPaymentMethodsHelper.getPaymentMethodLabelWithId(billInfo.PaymentMethod.toString());
                privateBillModel = privateBillModel.set("PaymentMethod", paymentMethodLabel);

                privateBillModel = privateBillModel
                    .set("Total", Math.round((billInfo.Total + 0.00001) * 100) / 100)
                    .set("QSTTotal", Math.round((billInfo.TotalQST + 0.00001) * 100) / 100)
                    .set("GSTTotal", Math.round((billInfo.TotalGST + 0.00001) * 100) / 100)
                    .set("HSTTotal", Math.round((billInfo.TotalHST + 0.00001) * 100) / 100)
                    .set("GenericTaxTotal", Math.round((billInfo.TotalGenericTax + 0.00001) * 100) / 100)
                    .set("Discount", Math.round((billInfo.Discount + 0.00001) * 100) / 100)
                    .set("Subtotal", Math.round((billInfo.Subtotal + 0.00001) * 100) / 100)
                    .set("QST", Math.round((billInfo.QST + 0.00001) * 100) / 100)
                    .set("GST", Math.round((billInfo.GST + 0.00001) * 100) / 100)
                    .set("HST", Math.round((billInfo.HST + 0.00001) * 100) / 100)
                    .set("GenericTax", Math.round((billInfo.GenericTax + 0.00001) * 100) / 100)
                    .set("SelectedBillSiteId", billInfo.SiteId);

                if (response.Data.PrivateBillInfo.EmployeeId) {
                    privateBillModel = privateBillModel.set("Provider", cacheStore.GetCliniciansPersonsByContactFacetId(response.Data.PrivateBillInfo.EmployeeId).NameWithPrefix);
                }

                switch (billInfo.DiscountType) {
                    case 0:
                        privateBillModel = privateBillModel.set("DiscountType", "$");
                        break;

                    case 1:
                        privateBillModel = privateBillModel.set("DiscountType", "%");
                        break;

                    default:
                        break;
                }

                const formattedDate = LuxonDateTimeParser.parse(privateBillInfo.InvalidationDate)?.toFormat("EEEE',' MMMM d',' yyyy", { locale: sessionStore.locale }) ?? "";
                privateBillModel = privateBillModel.set("InvalidDate", formattedDate);

                let employeeInvalidatedBy = cacheStore.GetEmployeeByContactFacetId(billInfo.InvalidatedByEmployeeId);
                if (!!employeeInvalidatedBy) {
                    let employeeTitleInvalidatedBy = cacheStore.GetLookupById(employeeInvalidatedBy.PrefixLookupId);
                    privateBillModel = privateBillModel.set("InvalidatedBy", employeeTitleInvalidatedBy.GetValue() + " " + employeeInvalidatedBy.LastName + ", " + employeeInvalidatedBy.FirstName);
                }
                privateBillModel = privateBillModel
                    .set("InvalidNote", billInfo.InvalidationNote)
                    .set("IsInvalid", billInfo.IsInvalid);

                if (cacheStore.ClinicSettings.EnablePrivateBillRecipient) {
                    const recipientDetails = new RecipientDetailsModel({
                        RecipientName: privateBillInfo.RecipientName,
                        RecipientAddress: privateBillInfo.RecipientAddress,
                        RecipientPhoneNumber: privateBillInfo.RecipientPhoneNumber,
                        RecipientFaxNumber: privateBillInfo.RecipientFaxNumber
                    });

                    privateBillModel = privateBillModel.set("RecipientDetails", recipientDetails);
                    privateBillModel = privateBillModel.set("BilledTo", privateBillInfo.BilledTo);
                }
            }
        }

        privateBillModel = privateBillModel.set("CanInvalidateBill", true);

        var totalAmount = 0;
        if (billedService != null) {
            let tableItems = response.Data.BilledService.map(n => {
                let itemModel: BilledServiceItem = new BilledServiceItem();

                itemModel.ServiceMultiplicator = n.Multiplicator;
                itemModel.ServiceName = itemModel.LoadServiceName(n.NameEn, n.NameFr, n.NameEs);

                itemModel.GST = n.GST;
                itemModel.QST = n.QST;
                itemModel.HST = n.HST;
                itemModel.GenericTax = n.GenericTax;
                itemModel.HasGST = n.HadGST;
                itemModel.HasQST = n.HadQST;
                itemModel.HasHST = n.HadHST;
                itemModel.HasGenericTax = n.HadGenericTax;
                itemModel.CostCenterId = n.CostCenterId;

                if (n.QuantityPaid > 0) {
                    privateBillModel = privateBillModel.set("CanInvalidateBill", false);
                }

                if (n.ServiceNumber)
                    itemModel.ServiceNumber = n.ServiceNumber;
                else
                    itemModel.ServiceNumber = "N/A";

                itemModel.ServicePrice = n.Price;

                totalAmount += n.Price * n.Multiplicator;

                return itemModel;
            });
            privateBillModel = privateBillModel.set("BilledService", Immutable.List<BilledServiceItem>(tableItems));
        }

        privateBillModel = privateBillModel.set("TotalAmount", Math.round((totalAmount + 0.00001) * 100) / 100);

        if (noteModel != null) {
            noteModel = noteModel.set("PrivateBillPopupModel", privateBillModel);
            store.PatientMedicalNoteModels[privateBillInfo.PatientContactId] = noteModel;
        }
    }

    private LoadPrivateBill_ForPrint(response: ServerEvent, store: PatientStore): void {
        this.LoadPrivateBill(response, store);
        var noteModel = store.PatientMedicalNoteModels[response.PatientId];
        var privateBillModel = noteModel.get("PrivateBillPopupModel");

        store.ActionsMedicalNote.PrintPrivateBill(privateBillModel.get("PrivateBillId"), true);
    }

    private CreateEncounter(response: ServerEvent, store: PatientStore): void {
        let patientContactId = response.Data.PatientContactId;

        let encounter: Encounter = response.Data.Encounter;

        let patientSummaryModel = store.PatientMedicalSummaryModels[patientContactId];

        if (cacheStore.ClinicSettings.EnableAutoLinkAppointmentToNote) {
            this.GetLinkedAppointmentId(patientContactId, encounter, store).then(id => {
                let noteModel = store.PatientMedicalNoteModels[patientContactId];
                if (noteModel) {
                    let noteModelEncounter = noteModel.get("Encounter");
                    noteModelEncounter.SuggestedAppointmentLinkId = id;
                    noteModel = noteModel.set("Encounter", noteModelEncounter);
                    store.PatientMedicalNoteModels[patientContactId] = noteModel;
                }
                encounter.SuggestedAppointmentLinkId = id;
            });
        }

        if (typeof patientSummaryModel !== "undefined" && typeof patientSummaryModel !== null) {
            let encounters = patientSummaryModel.get("Encounters").toArray();
            encounters.push(encounter); // toArray() never returns a null. If .get("Encounters") is null, this will fail regardless at ".toArray()"

            encounters = ArrayHelper.SortDynamic(encounters, "ConsultationDate", true, false, true);

            patientSummaryModel = patientSummaryModel.set("Encounters", Immutable.List(encounters));
            // patientSummaryModel = patientSummaryModel.set("IsNewNoteEnabled", true);
            patientSummaryModel = PatientMedicalSummaryHelper.FilterEncountersClientSide(patientSummaryModel, EncounterSearchIndexRebuildMode.RebuildOnEncounterListChange);

            store.ActionsMedicalNote.LoadEncounterForEdit(encounter.Id);
        }

        patientSummaryModel = patientSummaryModel.set("NewNoteWarningEncounterId", "");
        patientSummaryModel = patientSummaryModel.set("NewNoteWarningEncounterDate", null);
        patientSummaryModel = patientSummaryModel.set("ShowNewNoteWarning", false);
        sessionStore.hideGlobalOverlay();
        store.PatientMedicalSummaryModels[patientContactId] = patientSummaryModel;

        // Update the portfolio count on note creation.
        portfolioStore.PortfolioActions.LoadPortfolioCount();

        // Manage encounter patient related appointment states
        store.ActionsMedicalNote.ManagePatientAppointmentState(
            patientContactId,
            encounter.Id,
            encounter.ConsultationDate,
            AppointmentStatusChangeEventType.NewEncounter
        );

        this.updateSecondScreenModel(response.PatientId, response.Data.Encounter.Id);
    }

    private async GetLinkedAppointmentId(patientContactId: string, encounter: Encounter, store: PatientStore): Promise<string> {
        let appointmentModel = store.PatientHistoryAppointmentsModels[patientContactId];

        if (!appointmentModel.get("AppointmentListLoaded")) {
            const response = await Client.postAsync<any>(
                ServerOperations.Server.Myle_Patient_History_GetAppointment,
                {
                    PatientId: patientContactId
                }
            );

            if (response.errors.any() || response.data.AppointmentList == null)
                return;

            const appointments = PatientHelper.getAppointmentListFromResponse(response.data.AppointmentList);
            appointmentModel = appointmentModel
                .set("AppointmentList", Immutable.List<PatientAppointmentListItem>(appointments))
                .set("AppointmentListLoaded", true);
            store.PatientHistoryAppointmentsModels[patientContactId] = appointmentModel;
            appointmentModel = store.PatientHistoryAppointmentsModels[patientContactId];
        }

        const appointmentList = appointmentModel?.get("AppointmentList")?.toArray();
        return MedicalNoteHelper.AutoLinkAppointmentToEncounter(encounter, appointmentList);
    }

    private AddLabRequest(response: ServerEvent, store: PatientStore): void {
        const patientContactId = response.PatientId;
        let labRequest: RequestsInboxItem = response.Data.LabRequest;
        let patientNoteModel = patientStore.PatientMedicalNoteModels[patientContactId];

        if (!response.Data.Faxing && response.Data.Print === true)
            store.ActionsMedicalNote.PrintSelectedRequest({
                RequestId: labRequest.RequestId,
                FaxLabRequestComposite: null,
                MarkAsPrinted: true,
                AddSignature: response.Data.AddSignature
            });

        if (patientNoteModel == null)
            return;

        // ensure we display current employee as contributor to current note
        store.updateContributorsEncountersData(patientContactId, response.Data.EncounterId);

        patientNoteModel = patientNoteModel.set(
            "Requests",
            patientNoteModel.get("Requests")
                .upsert(x => x.RequestId == labRequest.RequestId, labRequest)
        );

        if (response.Data.Faxing === true) {
            patientNoteModel = patientNoteModel.set("LabRequestFaxResponse", null);
            patientNoteModel = patientNoteModel.set("IsLabFaxPopupOpen", true);
            patientNoteModel = patientNoteModel.set("LabRequestFaxModel", labRequest);

            patientStore.PatientMedicalNoteModels[patientContactId] = patientNoteModel;
            store.ActionsMedicalNote.PrintSelectedRequest(
                {
                    RequestId: labRequest.RequestId,
                    FaxLabRequestComposite: null,
                    MarkAsPrinted: true,
                    AddSignature: false
                },
                fileName => {
                    patientStore.PatientMedicalNoteModels[patientContactId] = patientStore.PatientMedicalNoteModels[patientContactId]
                        .set("IsLabFaxPopupOpen", true)
                        .set("LabRequestPreviewPDFURLFileName", fileName)
                        .set("LabRequestPreviewPDFURL", PrintHelper.GetPreviewPath(fileName))
                        .set("PrintSignaturePreviewFileName", fileName);
                }
            );
        }

        patientStore.PatientMedicalNoteModels[patientContactId] = patientNoteModel;

        sessionStore.hideGlobalOverlay();
    }

    private updateConsultationDetails(response: ServerEventWithRequest<IMyle_Patient_MedicalNote_GetUpdateConsultationRequest>, store: PatientStore): void {
        let patientContactId: Guid = response.Data.PatientContactId;
        let encounterId: Guid = response.requestDto.EncounterId;
        let consultationDate = response.requestDto.ConsultationDate;
        let consultationTypeId = response.requestDto.ConsultationTypeLookupId;
        let consultationContextId = response.requestDto.ConsultationContextLookupId;
        let consultationSiteId = response.requestDto.ConsultationSiteId;
        let consultationDepartmentId: Guid = response.requestDto.ConsultationDepartmentId;
        let consultationIsActiveSite = response.requestDto.SetConsultationSiteAsActive;
        const enableAppointmentLinkSuggestions = response.requestDto.EnableAppointmentLinkSuggestions;

        // ensure we display current employee as contributor to current note
        store.updateContributorsEncountersData(patientContactId, encounterId);

        let patientNoteModel = patientStore.PatientMedicalNoteModels[response.PatientId];

        if (!(patientNoteModel == null)) {
            let encounter = patientNoteModel.get("Encounter");

            if (encounter && encounter.Id == encounterId) {
                // update encounter in open note
                encounter.ConsultationTypeLookupId = consultationTypeId;
                encounter.ConsultationContextLookupId = consultationContextId;
                encounter.CostCenterId = consultationDepartmentId;
                if (consultationSiteId != null && consultationSiteId != "")
                    encounter.SiteId = consultationSiteId;

                encounter.ConsultationDate = consultationDate;
                if (!encounter.IsSignedOff && cacheStore.ClinicSettings.EnableAutoLinkAppointmentToNote) {
                    encounter.AppointmentId = response.requestDto.AppointmentId;
                    encounter.SuggestedAppointmentLinkId = (enableAppointmentLinkSuggestions)
                        ? MedicalNoteHelper.AutoLinkAppointmentToEncounter(
                            encounter,
                            store.PatientHistoryAppointmentsModels[patientContactId]?.get("AppointmentList")?.toArray()
                        )
                        : null;
                }

                patientNoteModel = patientNoteModel.delete("Encounter");
                patientNoteModel = patientNoteModel.set("Encounter", encounter);

                // update encounter in encounter list/ summary
                let summaryModel = patientStore.PatientMedicalSummaryModels[patientContactId];
                let encounters = summaryModel.get("Encounters").toArray();
                let found: Encounter = ArrayHelper.Find(encounters, x => x.Id == encounterId);
                let idx = encounters.indexOf(found);

                found.ConsultationDate = consultationDate;
                found.ConsultationTypeLookupId = consultationTypeId;
                found.ConsultationContextLookupId = consultationContextId;
                encounters[idx] = found;

                summaryModel.set("Encounters", Immutable.List(encounters));
                patientStore.PatientMedicalSummaryModels[patientContactId] = summaryModel;
            }

            patientNoteModel = patientNoteModel.set("IsEditConsultationDetailsPopupVisible", false);
            patientNoteModel = patientNoteModel.set("IsModificationLoading", false);
            patientStore.PatientMedicalNoteModels[patientContactId] = patientNoteModel;
        }
        if (consultationIsActiveSite) {
            var currentUser = sessionStore.getCurrentEmployee().Model;
            Dispatcher.dispatch(LocalOperations.Sites.SetActiveSite, { ActiveSiteId: consultationSiteId }, 0);
            currentUser.ActiveDepartmentId = consultationDepartmentId;
            cacheStore.RebindSingleEmployeeToCache(currentUser);
            let emp = EmployeeUIModel.fromCompositeObject(currentUser);

            if (emp.Model.Contact_id == sessionStore.individualContactId) {
                sessionStore.rebindCurrentEmployee(emp);
                settingsStore.configuration = settingsStore.configuration.set("ActiveSite", consultationSiteId);
                settingsStore.configuration = settingsStore.configuration.set("ActiveDepartment", consultationDepartmentId);
                settingsStore.configuration = settingsStore.configuration.set("CostCentersList", cacheStore.getCostCenterBySiteId(consultationSiteId));
            }
        }
        sessionStore.hideGlobalOverlay();

        if (patientNoteModel.get("ViewMode") == MedicalNoteViewMode.Edit) {
            store.ActionsMedicalNote.LoadEncounterForEdit(encounterId);
        }
        else {
            store.ActionsMedicalNote.LoadEncounterForView(encounterId);
        }
    }

    private GetEncounterStatus(response: ServerEventWithThen<IMyle_Patient_MedicalNote_GetStatusRequest, () => void>, store: PatientStore): void {
        const patientContactId = response.Data.PatientContactId;
        const signOffDate = response.Data.SignOffDate;
        const signedOffByEmployeeId = response.Data.SignedOffByEmployeeId;
        const lostConcurrency = signedOffByEmployeeId?.length > 0;
        let encounterModel = store.PatientMedicalNoteModels[patientContactId];

        if (lostConcurrency) {
            // Someone else approuved it already.
            encounterModel = encounterModel.set("ConcurrentEditSignedOffBy", signedOffByEmployeeId);
            encounterModel = encounterModel.set("ConcurrentEditSignedOffDate", signOffDate);
            encounterModel = encounterModel.set("ConcurrentEditShowWarningPopup", true);
            encounterModel = encounterModel.set("ConcurrenEditIsForMedicalItem", response.requestDto.callerIdentifier == "saveMedicalItem");
        }
        else {
            // Available for Save
            encounterModel = encounterModel.set("ConcurrentEditSignedOffBy", "");
            encounterModel = encounterModel.set("ConcurrentEditSignedOffDate", null);
            encounterModel = encounterModel.set("ConcurrentEditShowWarningPopup", false);
            encounterModel = encounterModel.set("ConcurrenEditIsForMedicalItem", false);
        }

        store.PatientMedicalNoteModels[patientContactId] = encounterModel;

        if (!lostConcurrency && response.then != null)
            response.then(); // Sucess - Available for Save, Apply Callback
    }

    private CreateNewForm(response: ServerEvent, store: PatientStore): void {
        let resp: PatientForms = response.Data;
        resp.contextOpen = false; // add new property for front-end use

        let encounter = patientStore.PatientMedicalNoteModels[response.PatientId].get("Encounter");

        let list = store.PatientMedicalNoteModels[response.PatientId].get("PatientForms");
        let replacementList = list.push(PdfFormInboxItem.fromPatientForms(resp, encounter.Id));

        // ensure we display current employee as contributor to current note
        store.updateContributorsEncountersData(response.PatientId, resp.EncounterId);

        store.PatientMedicalNoteModels[response.PatientId] = store.PatientMedicalNoteModels[response.PatientId].set("PatientForms", replacementList);

        window.setTimeout(() => {
            Client.openRadpdfForm(
                resp.Id,
                cacheStore.GetCurrentEmployee().ContactFacet_id,
                encounter.Id,
                encounter.ConsultationDate,
                true,
                null,
                () => { }
            );
        }, 50);

        store.EmitChangeToListeners(); // Weird, form wont refresh in Encounter if we dont specify this here, probably related to the Client Call above
    }

    private onDeletePdfForm(response: ServerEvent, store: PatientStore): void {
        var resp = response.Data;
        var list = store.PatientMedicalNoteModels[response.PatientId].get("PatientForms");

        // ensure we display current employee as contributor to current note
        store.updateContributorsEncountersData(response.PatientId, resp.EncounterId);

        var newList = list
            .filter(item => item.Id !== resp.PatientFormId)
            .toList();

        store.PatientMedicalNoteModels[response.PatientId] = store.PatientMedicalNoteModels[response.PatientId].set("PatientForms", newList);
    }

    private onPatientFormIsPersistant(response: ServerEventWithRequest<IMyle_Patient_MedicalNote_GetFormPersistRequest>, store: PatientStore): void {
        let forms = store.PatientMedicalNoteModels[response.PatientId].get("PatientForms");

        let updatedList = forms.map(f => {
            // ensure we display current employee as contributor to current note
            store.updateContributorsEncountersData(response.PatientId, response.requestDto.EncounterId);

            if (f.Id === response.Data.PatientFormId) {
                f.IsPersistant = response.Data.IsPersistant;
            }
            return f;
        });
        store.PatientMedicalNoteModels[response.PatientId] = store.PatientMedicalNoteModels[response.PatientId].set("PatientForms", updatedList.toList());
    }

    private SaveEncounter(response: ServerEventWithThen<any, () => void>, store: PatientStore): void {
        this.LoadEncounter(response, store, MedicalNoteViewMode.View);
        this.updateSecondScreenModel(response.PatientId, response.Data.Encounter.Id);
        patientStore.ActionsMedicalSummary.loadEncounterFormsTitles(response.PatientId);
    }

    private SaveAsNewEncounter(response: ServerEventWithThen<any, () => void>, store: PatientStore): void {
        this.LoadEncounter(response, store, MedicalNoteViewMode.View);
        this.updateSecondScreenModel(response.PatientId, response.Data.Encounter.Id);
    }

    private SaveHandNoteFile(response: ServerEvent, store: PatientStore): void {
        let imgInfo = response.Data.ImageInfo as FreeFormImage;

        // ensure we display current employee as contributor to current note
        store.updateContributorsEncountersData(response.PatientId, imgInfo.EncounterId);

        let patientNoteModel = store.PatientMedicalNoteModels[response.PatientId];

        if (imgInfo.EncounterId == patientNoteModel.get("Encounter").Id) {
            const list = patientNoteModel.get("HandwrittenNotes").toArray();
            if (list.any(p => p.Id === imgInfo.Id))
                return;

            const newList = [...list];
            newList.push(imgInfo);
            patientNoteModel = patientNoteModel.set("HandwrittenNotes", Immutable.List(newList));
            store.PatientMedicalNoteModels[response.PatientId] = patientNoteModel;
        }
        this.updateSecondScreenModel(response.PatientId, imgInfo.EncounterId);
    }

    private DeleteHandNoteFile(response: ServerEvent, store: PatientStore): void {
        let deleted = response.Data.IsDeleted as boolean;
        let imageId = response.Data.RemovedImage as string;
        if (deleted) {
            // ensure we display current employee as contributor to current note
            store.updateContributorsEncountersData(response.PatientId, response.Data.EncounterId);

            let patientNoteModel = store.PatientMedicalNoteModels[response.PatientId];
            let newList = patientNoteModel.get("HandwrittenNotes").filter(n => n.Id !== imageId).toList();
            patientNoteModel = patientNoteModel.set("HandwrittenNotes", newList);
            store.PatientMedicalNoteModels[response.PatientId] = patientNoteModel;
            this.updateSecondScreenModel(response.PatientId, patientNoteModel.get("Encounter").Id);
        }
    }

    private SignoffEncounter(response: ServerEventWithThen<any, () => void>, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[response.PatientId];
        patientStore.PatientUIModels[response.PatientId] = patientStore.PatientUIModels[response.PatientId]
            .set("IsNoteApprovalInProgress", false)
            .set("FormsBeingSentOnNoteApproval", []);

        if (
            !response.Data.AlreadySignedOff &&
            (response.Data.HasError || response.Data.IsSuccess === false) &&
            response.Errors?.some(error => error.Code === FlowsheetConstants.ErrorCodes.SnapshotGenerationError)
        ) {
            sessionStore.showPopup({
                title: cacheStore.GetString("ErrorLabel"),
                content: cacheStore.GetString("Flowsheets_SnapshotGenerationErrorLabel"),
                showOk: true,
                showCancel: false
            });
            response.then?.();
            return;
        }

        if (response.Data.AlreadySignedOff) {
            // show error notification popup
            noteModel = noteModel.set("AlreadySignedOffShowWarningPopup", true);
            noteModel = noteModel.set("AlreadySignedOffBy", response.Data.AlreadySignedOffByEmployeeId);
            noteModel = noteModel.set("AlreadySignedOffDate", response.Data.AlreadySignedOffByDate);
            sessionStore.showGlobalOverlay(true);
            patientStore.PatientMedicalNoteModels[response.PatientId] = noteModel;
        }
        else {
            const encounterId: Guid = response.Data.EncounterId;

            // ensure we display current employee as contributor to current note
            store.updateContributorsEncountersData(response.PatientId, encounterId);

            // reload encounter in view mode
            store.ActionsMedicalNote.LoadEncounterForView(encounterId);

            let summaryModel = patientStore.PatientMedicalSummaryModels[response.PatientId];

            // Commit Medication locally because they just got committed
            const meds = summaryModel.get("MedicalItemsMedicationAndPrescription");
            if (meds != null) {
                meds.forEach(x => {
                    if (x.Model.RelatedEncounterId == encounterId) {
                        x.Model.IsCommitted = true;

                        if (x.Model.CommitDate == null)
                            x.Model.CommitDate = moment(Date.now()).toDate();
                    }
                });
            }

            summaryModel = summaryModel.set("MedicalItemsMedicationAndPrescription", meds);
            patientStore.PatientMedicalSummaryModels[response.PatientId] = summaryModel;

            // ensure all current medical items related to encounter are set to commited
            let itemsModel = patientStore.PatientMedicalItemsModels[response.PatientId];
            if (itemsModel != null) {
                const medicalItemsList = itemsModel.get("MedicalItemsList");
                const allMedicalItemsList = itemsModel.get("AllMedicalItemsList");

                if (medicalItemsList != null) {
                    medicalItemsList.forEach(x => {
                        if (x.RelatedEncounterId == encounterId)
                            x.IsCommitted = true;
                    });
                }

                itemsModel = itemsModel.set("MedicalItemsList", Immutable.List(medicalItemsList));

                if (allMedicalItemsList != null) {
                    allMedicalItemsList.forEach(x => {
                        if (x.RelatedEncounterId == encounterId)
                            x.IsCommitted = true;
                    });
                }

                itemsModel = itemsModel.set("AllMedicalItemsList", Immutable.List(allMedicalItemsList));
                patientStore.PatientMedicalItemsModels[response.PatientId] = itemsModel;
            }

            this.updateSecondScreenModel(response.PatientId, encounterId);

            if (cacheStore.ClinicSettings.EnableChronicDiseaseManagement) {
                patientStore.FlowsheetModels[response.PatientId] = patientStore.FlowsheetModels[response.PatientId].filter(
                    p => p.get("encounterId") !== encounterId
                );

                store.FlowsheetActions.loadFlowsheets(encounterId, response.PatientId);
            }
        }

        response.then?.();
    }

    private PrintPrescription(response: ServerEventWithThen<any, (filename: string) => void>): void {
        let fileName = response.Data.FileName;

        response.Data.IsSuccess
            && response.then
            && response.then(fileName);
    }

    private PrintMedicalNote(response: ServerEvent): void {
        var fileName = response.Data.FileName;

        if (response.Data.IsSuccess) {
            PrintHelper.ShowPrintPreviewFileName(fileName);
        }
    }

    private FinishPrivateBillSave(response: ServerEvent, store: PatientStore): void {
        if (response.Data.Success) {
            var noteModel = store.PatientMedicalNoteModels[response.PatientId];

            var shouldPrint = noteModel.get("ShouldPrintBillAfterSave");
            if (shouldPrint) {
                store.ActionsMedicalNote.PrintPrivateBill(response.Data.SavedPrivateBill.Id, true);
            }
            else {
                var privateBillModel = noteModel.get("PrivateBillPopupModel");
                privateBillModel = privateBillModel.set("IsNewBill", false);
                noteModel = noteModel.set("PrivateBillPopupModel", privateBillModel);
            }

            noteModel = noteModel.set("IsPrivateBillEditPopupOpen", false);
            noteModel = noteModel.set("IsPrivateBillCreationWarningPopupOpen", false);
            noteModel = noteModel.set("ShouldPrintBillAfterSave", false);
            noteModel = noteModel.set("IsPrivateBillSaveLocked", false);

            store.PatientMedicalNoteModels[response.PatientId] = noteModel;
        }

        store.ActionsMedicalNote.LoadPrivateBillList(store.PatientUIModels[response.PatientId].get("PatientContactFacetId"), "", null, null, false, noteModel.get("Encounter").Id);
    }

    private GeneratePatientPrivateBillPDF(response: ServerEvent, store: PatientStore): void {
        var noteModel = store.PatientMedicalNoteModels[response.PatientId];
        noteModel = noteModel.set("IsPrivateBillEditPopupOpen", false);
        noteModel = noteModel.set("IsPrivateBillPopupOpen", false);
        noteModel = noteModel.set("IsPrivateBillCreationWarningPopupOpen", false);
        noteModel = noteModel.set("ShouldPrintBillAfterSave", false);
        store.PatientMedicalNoteModels[response.PatientId] = noteModel;

        const fileName = response.Data["FileName"];
        window.open(apiSettings.buildAppUrl(`api/appointments/fetch/${fileName}.pdf`, { auth: true }), "_blank");
    }

    private CompleteInvalidationBill(response: ServerEvent, store: PatientStore): void {
        var noteModel = store.PatientMedicalNoteModels[response.PatientId];
        var privateBillModel = noteModel.get("PrivateBillPopupModel");
        noteModel = noteModel.set("ShowInvalidBillPopup", false);
        store.ActionsMedicalNote.LoadPrivateBillInfo(response.Data.PrivateBillId);
        noteModel = noteModel.set("PrivateBillPopupModel", privateBillModel);
        store.PatientMedicalNoteModels[response.PatientId] = noteModel;

        store.ActionsMedicalNote.LoadPrivateBillList(store.PatientUIModels[response.PatientId].get("PatientContactFacetId"), "", null, null, false, noteModel.get("Encounter").Id);
    }

    private LoadCostCenter(response: ServerEvent, store: PatientStore): void {
        var responseModel: PrivateBillCostCenter[] = response.Data.CostCenters;

        var noteModel = store.PatientMedicalNoteModels[response.PatientId];
        var privateBillModel = noteModel.get("PrivateBillPopupModel");

        if (responseModel != null) {
            privateBillModel = privateBillModel.set("CostCenterList", Immutable.List<PrivateBillCostCenter>(responseModel));
        }
        else
            privateBillModel = privateBillModel.set("CostCenterList", Immutable.List<PrivateBillCostCenter>([]));

        if (responseModel != null && responseModel.length > 0) {
            if (!IsNullOrWhiteSpace(sessionStore.cachedPrivateBillCostCenterId))
                privateBillModel = privateBillModel.set("SelectedCostCenterId", sessionStore.cachedPrivateBillCostCenterId);
            else
                privateBillModel = privateBillModel.set("SelectedCostCenterId", responseModel[0].ID);
        }

        if (!privateBillModel.get("ShowDetailsOrNewBill")) {
            privateBillModel = privateBillModel.set("ShowDetailsOrNewBill", true);
        }
        if (!privateBillModel.get("IsNewBill")) {
            privateBillModel = privateBillModel.set("IsNewBill", true);
        }

        noteModel = noteModel.set("PrivateBillPopupModel", privateBillModel);
        store.PatientMedicalNoteModels[response.PatientId] = noteModel;
    }

    private LoadPendingStatus(response: ServerEvent, store: PatientStore): void {
        var noteModel = store.PatientMedicalNoteModels[response.PatientId];
        if (response.Data["Success"]) {
            var supervisorId: string = response.Data["SupervisorContactId"];

            let employee = cacheStore.GetCliniciansPersonsByContactId(supervisorId);
            let employeeName = employee.NameWithPrefix + (employee.EmployeeModel.Licences != null && employee.EmployeeModel.Licences.length > 0 ? " (" + employee.EmployeeModel.Licences[0].LicenceNumber + ") " : "");

            noteModel = noteModel.set("SupervisorName", employeeName);
        }

        store.PatientMedicalNoteModels[response.PatientId] = noteModel;
    }

    private SendPendingApproval(response: ServerEvent, store: PatientStore): void {
        if (response.Data["Success"]) {
            const supervisorId: string = response.Data["SupervisorId"];
            const employee = cacheStore.GetCliniciansPersonsByContactId(supervisorId);
            const employeeName = employee.NameWithPrefix + (employee.EmployeeModel.Licences != null && employee.EmployeeModel.Licences.length > 0 ? " (" + employee.EmployeeModel.Licences[0].LicenceNumber + ") " : "");

            store.PatientMedicalNoteModels[response.PatientId] = store.PatientMedicalNoteModels[response.PatientId]
                .set("ShowApprovalSupervisorPopup", false)
                .set("SupervisorName", employeeName);

            overlayStore.hideOverlay("global-overlay");

            const nextAllowlist = response.Data["NextAllowlistModel"];
            if (nextAllowlist) // it's very important to not update the cache if `response.NextAllowlistModel` is null!
                cacheStore.updateCurrentEmployeeMedicalNoteAllowlistCache(nextAllowlist);
        }
    }

    private faxLabRequest(response: ServerEventWithThen<any, () => void>, store: PatientStore): void {
        const faxResponse: GenericResponse = response.Data;

        const patientContactId = response.PatientId;
        let patientNoteModel = store.PatientMedicalNoteModels[patientContactId];

        patientNoteModel = patientNoteModel
            .set("IsLabFaxPopupOpen", false)
            .set("LabRequestFaxResponse", faxResponse);

        store.PatientMedicalNoteModels[patientContactId] = patientNoteModel;
        sessionStore.showGlobalOverlay(true);

        response.then && response.then();
    }

    private PrintSelectedRequest(response: ServerEventWithThen<IPrintLabRequest, (fileName: string) => void>, store: PatientStore): void {
        let requestId = response.Data.RequestId;
        if (!requestId)
            return;

        let fileName = response.Data.FileName;

        let patientNoteModel = store.PatientMedicalNoteModels[response.PatientId];
        store.PatientMedicalNoteModels[response.PatientId] = patientNoteModel.set(
            "Requests",
            patientNoteModel.get("Requests").mutate(
                x => {
                    x.IsFaxed = response.requestDto.MarkAsPrinted && response.requestDto.FaxLabRequestComposite && !IsNullOrWhiteSpace(response.requestDto.FaxLabRequestComposite.LabFax);
                    x.IsPrinted = response.requestDto.MarkAsPrinted;
                    x.FirstPrintedByEmployeeId = response.requestDto.MarkAsPrinted && response.Data["FirstPrintedByEmployeeId"];
                    x.FirstPrintedDate = response.requestDto.MarkAsPrinted && response.Data["FirstPrintedDate"];
                },
                x => x.RequestId === requestId
            )
        );

        store.updateScreeningsModelAfterPrintingRequest(requestId, MedicalRequestType.LabRequest);

        response.then && response.then(fileName);
    }

    private PrintConsultationRequest(response: ServerEventWithThen<IPrintConsultationRequest, (fileName: string) => void>, store: PatientStore): void {
        let requestId = response.Data.ConsultationRequestId;
        if (!requestId)
            return;

        let fileName = response.Data.FileName;

        let patientNoteModel = store.PatientMedicalNoteModels[response.PatientId];
        store.PatientMedicalNoteModels[response.PatientId] = patientNoteModel
            .set(
                "ConsultationRequests",
                patientNoteModel.get("ConsultationRequests").mutate(
                    x => {
                        x.IsPrinted = response.requestDto.MarkAsPrinted;
                        x.IsFaxed = response.requestDto.MarkAsFaxed; /* We should update that when actually faxing, not when generating file for Fax */
                    },
                    x => x.ConsultationRequestId === requestId
                )
            );

        store.updateScreeningsModelAfterPrintingRequest(requestId, MedicalRequestType.ConsultationRequest);

        response.then && response.then(fileName);
    }

    private FaxConsultationRequest(response: ServerEventWithThen<any, () => void>, store: PatientStore): void {
        var faxResponse: GenericResponse = response.Data;

        var patientContactId = response.PatientId;
        var patientNoteModel = store.PatientMedicalNoteModels[patientContactId];
        patientNoteModel = patientNoteModel.set("IsConsultationRequestFaxPopupOpen", false);
        patientNoteModel = patientNoteModel.set("ConsultationRequestFaxResponse", faxResponse);
        store.PatientMedicalNoteModels[patientContactId] = patientNoteModel;
        sessionStore.showGlobalOverlay(true);
        response.then && response.then();
    }

    private LoadLightPrivateBill(response: ServerEvent, store: PatientStore): void {
        const privateBillList: LightPrivateBill[] = response.Data["PrivateBillList"];
        let patientNoteModel = store.PatientMedicalNoteModels[response.PatientId];
        const currentSelectedId = patientNoteModel.get("SelectedReusedPrivateBillId");

        patientNoteModel = patientNoteModel.set("ShowReusingBillPopup", true);

        if (privateBillList) {
            patientNoteModel = patientNoteModel.set("ReusingPrivateBillsList", privateBillList.toImmutable());
        }
        else {
            patientNoteModel = patientNoteModel.set("ReusingPrivateBillsList", [].toImmutable());
        }

        if (!privateBillList?.any(p => p.PrivateBillId === currentSelectedId)) {
            patientNoteModel = patientNoteModel.set("SelectedReusedPrivateBillId", null)
                .set("SelectedReusedPrivateBillFileName", null);
        }

        store.PatientMedicalNoteModels[response.PatientId] = patientNoteModel;

        sessionStore.showGlobalOverlay(true);
    }

    private LoadListPrivateBillForEncounter(response: ServerEvent, store: PatientStore): void {
        var privateBillList: LightPrivateBill[] = response.Data["PrivateBillList"];
        var patientNoteModel = store.PatientMedicalNoteModels[response.PatientId];

        patientNoteModel = patientNoteModel.set("PrivateBills", privateBillList.toImmutable());

        store.PatientMedicalNoteModels[response.PatientId] = patientNoteModel;
    }

    private GeneratePrivateBillForReuse(response: ServerEvent, store: PatientStore): void {
        var fileName = response.Data["FileName"];
        var patientNoteModel = store.PatientMedicalNoteModels[response.PatientId];

        if (!IsNullOrWhiteSpace(fileName)) {
            patientNoteModel = patientNoteModel.set("SelectedReusedPrivateBillFileName", fileName);
        }

        store.PatientMedicalNoteModels[response.PatientId] = patientNoteModel;
    }

    private LoadPrivateBillInfoForReuse(response: ServerEvent, store: PatientStore): void {
        var data: PatientSelectedBillInfoResponse = new PatientSelectedBillInfoResponse();

        data.PrivateBillInfo = response.Data["PrivateBillInfo"];
        data.BilledService = response.Data["BilledService"];

        if (data.PrivateBillInfo) {
            this.fillBillData(data, response, store, true);
        }
    }

    private LoadPrivateBillInfoForEdit(response: ServerEvent, store: PatientStore): void {
        const data: PatientSelectedBillInfoResponse = new PatientSelectedBillInfoResponse();

        data.PrivateBillInfo = response.Data["PrivateBillInfo"];
        data.BilledService = response.Data["BilledService"];
        data.PrivateBillId = response.Data["PrivateBillId"];

        if (data.PrivateBillInfo) {
            this.fillBillData(data, response, store, false);
        }
    }

    private fillBillData(data: PatientSelectedBillInfoResponse, response: ServerEvent, store: PatientStore, isFromReuse: boolean): void {
        let patientNoteModel = store.PatientMedicalNoteModels[response.PatientId];
        let privateBillModel = patientNoteModel.get("PrivateBillPopupModel");

        privateBillModel = privateBillModel.set("CancelPopupIsOpen", false);

        if (cacheStore.SiteCache.length > 1) {
            const siteUIModels: SiteUIModel[] = [];
            const currentEmployeeSites = sessionStore.getCurrentEmployee().Model.AssociatedSites;

            for (let siteComposite of cacheStore.SiteCache) {
                sessionStore.getCurrentEmployee().Model.AssociatedSites.forEach(p => {
                    if (p === siteComposite.Model.Id) {
                        let site = SiteUIModel.FromCompositeObject(siteComposite);
                        siteUIModels.push(site);
                    }
                });
            }

            if (!currentEmployeeSites.any(s => s == data.PrivateBillInfo.SiteId)) {
                let createdEmployeeSite = SiteUIModel.FromCompositeObject(cacheStore.SiteCache.find(x => x.Model.Id == data.PrivateBillInfo.SiteId));
                createdEmployeeSite.Checked = true;
                siteUIModels.push(createdEmployeeSite);
            }

            const sites = Immutable.List(siteUIModels);
            privateBillModel = privateBillModel.set("Sites", sites);
            privateBillModel = privateBillModel.set("Clinicians", CommonHelper.GetEmployeesBasedOnSite(Immutable.List(cacheStore.GetClinicians(false)), sites.toArray(), data.PrivateBillInfo.SiteId));
        }
        else {
            privateBillModel = privateBillModel.set("Clinicians", Immutable.List(cacheStore.GetClinicians(false)));
            privateBillModel = privateBillModel.set("SelectedSiteId", data.PrivateBillInfo.SiteId);
        }

        privateBillModel = privateBillModel.set("SelectedSiteId", data.PrivateBillInfo.SiteId);

        const person = privateBillModel.get("Clinicians").toArray().filter((c: Person) => c.ContactFacetId === data.PrivateBillInfo.EmployeeId);
        privateBillModel = privateBillModel.set("SelectedClinicians", person.toImmutable());

        const employee = cacheStore.GetEmployeeByContactFacetId(data.PrivateBillInfo.EmployeeId);
        if (!!employee) {
            const employeeTitle = cacheStore.GetLookupById(employee.PrefixLookupId);
            privateBillModel = privateBillModel.set("SelectedClinicianName", `${employeeTitle.GetValue()} ${employee.LastName}, ${employee.FirstName}`);
        }

        const site = cacheStore.SiteCache.filter(n => n.Model.Id === data.PrivateBillInfo.SiteId);
        privateBillModel = privateBillModel.set("GSTPercent", site[0].Model.GstValue);
        privateBillModel = privateBillModel.set("QSTPercent", site[0].Model.QstValue);
        privateBillModel = privateBillModel.set("HSTPercent", site[0].Model.HstValue);
        privateBillModel = privateBillModel.set("GenericTaxPercent", site[0].Model.GenericTaxValue);

        privateBillModel = privateBillModel.set("SelectedDateOfVisit", data.PrivateBillInfo.VisitDate);

        privateBillModel = privateBillModel.set("SelectedPaymentMethod", data.PrivateBillInfo.PaymentMethod);

        if (cacheStore.ClinicSettings.EnablePrivateBillRecipient) {
            const recipientDetails = new RecipientDetailsModel({
                RecipientName: data.PrivateBillInfo.RecipientName,
                RecipientAddress: data.PrivateBillInfo.RecipientAddress,
                RecipientPhoneNumber: data.PrivateBillInfo.RecipientPhoneNumber,
                RecipientFaxNumber: data.PrivateBillInfo.RecipientFaxNumber
            });

            privateBillModel = privateBillModel.set("SelectedBillRecipientDetails", recipientDetails)
                .set("RecipientDetails", recipientDetails)
                .set("SelectedBillBilledTo", data.PrivateBillInfo.BilledTo)
                .set("BilledTo", data.PrivateBillInfo.BilledTo);
        }

        const newBillArray = [];
        data.BilledService.map(n => {
            var service = new BillableServiceDropdownItem();
            service.Service.BillableServiceId = n.ServiceNumber != "N/A" ? GuidHelper.NewGuid() : GuidHelper.Empty();
            service.Service.Price = n.Price;
            service.Service.HasGST = n.HadGST;
            service.Service.HasQST = n.HadQST;
            service.Service.HasHST = n.HadHST;
            service.Service.HasGenericTax = n.HadGenericTax;
            service.Service.IsFromServiceList = n.ServiceNumber != "N/A";
            service.Service.NameEn = n.NameEn;
            service.Service.NameFr = n.NameFr;
            service.Service.NameEs = n.NameEs;
            service.Service.Number = n.ServiceNumber;
            service.Service.CostCenterId = n.CostCenterId;

            var item = {
                Id: GuidHelper.NewGuid(),
                Qty: n.Multiplicator,
                SelectedService: service,
                Services: null,
                Price: n.Price,
                HasGST: n.HadGST,
                HasQST: n.HadQST,
                HasHST: n.HadHST,
                HasGenericTax: n.HadGenericTax,
                GST: n.GST,
                QST: n.QST,
                HST: n.HST,
                GenericTax: n.GenericTax,
                IsFromServiceList: n.ServiceNumber != "N/A",
                ServiceNumber: n.ServiceNumber,
                NameFr: n.NameFr,
                NameEn: n.NameEn,
                NameEs: n.NameEs
            };

            var pushItem: IBillableService = Immutable.fromJS(item);
            newBillArray.push(pushItem);
        });

        const emptyItem = {
            Id: GuidHelper.NewGuid(),
            Qty: 1,
            SelectedService: null,
            Services: null,
            Price: 0.00,
            HasGST: false,
            HasQST: false,
            HasHST: false,
            HasGenericTax: false,
            IsFromServiceList: false
        };

        const pushEmptyItem: IBillableService = Immutable.fromJS(emptyItem);
        newBillArray.push(pushEmptyItem);

        privateBillModel = privateBillModel.set("IsSave", true)
            .set("ShowDetailsOrNewBill", true)
            .set("IsNewBill", isFromReuse)
            .set("IsEditBill", !isFromReuse)
            .set("PrivateBillId", isFromReuse === true ? GuidHelper.NewGuid() : data.PrivateBillId)
            .set("BillNumber", "0")
            .set("IsInvalid", false)
            .set("NewBillDiscount", data.PrivateBillInfo.Discount)
            .set("Notes", data.PrivateBillInfo.Notes)
            .set("IsCashSelected", data.PrivateBillInfo.DiscountType === 0);

        patientNoteModel = patientNoteModel.set("PrivateBillBillableServices", newBillArray.toImmutable())
            .set("PrivateBillPopupModel", privateBillModel)
            .set("IsPrivateBillEditPopupOpen", true)
            .set("ShowReusingBillPopup", false);

        store.PatientMedicalNoteModels[response.PatientId] = patientNoteModel;

        this.CalculateSubtotalAndTotal(response.PatientId, store);

        if (sessionStore.getCurrentEmployee().isActiveSiteMexico()) {
            store.ActionsMedicalNote.LoadCostCenterForSite(privateBillModel.get("SelectedBillSiteId"));
        }
    }

    private CalculateSubtotalAndTotal(patientId: string, store: PatientStore): void {
        PatientMedicalNoteHandler.calculateSubtotalAndTotalStatic(patientId, store);
    }

    private printCharts(response: ServerEventWithThen<any, (fileName: string) => void>): void {
        var fileName = response.Data["FileName"];

        PrintHelper.ShowPrintPreviewFileName(fileName);
    }

    private UnapproveMedicalEncounter(response: ServerEvent, store: PatientStore): void {
        let activePatientId = response.PatientId;
        let noteModel = store.PatientMedicalNoteModels[activePatientId];
        let summaryModel = store.PatientMedicalSummaryModels[activePatientId];

        noteModel = noteModel.set("ShowUnapproveNoteWarningPopup", false);
        sessionStore.hideGlobalOverlay();

        if (response.Data.Success == true) {
            store.ActionsMedicalSummary.LoadPatientCumulativeProfile(activePatientId, null, true, true, "PatientMedicalNoteResponseHandler_UnapproveMedicalNote"); // seems overkill
            store.ActionsMedicalNote.LoadEncounterForEdit(response.Data.EncounterId);
        }
        else if (response.Data.Success == false) {
            noteModel = noteModel.set("MedicalNoteErrorWarningPopup", true);

            if (response.Data.MedicalItems.length > 0) {
                noteModel = noteModel.set("MedicalItemsErrorList", response.Data.MedicalItems);
            }
        }

        store.PatientMedicalSummaryModels[response.PatientId] = summaryModel;
        store.PatientMedicalNoteModels[response.PatientId] = noteModel;
        this.updateSecondScreenModel(response.PatientId, response.Data.EncounterId);

        if (cacheStore.ClinicSettings.EnableChronicDiseaseManagement) {
            const encounterId = response.Data.EncounterId;
            patientStore.FlowsheetModels[response.PatientId] = patientStore.FlowsheetModels[response.PatientId].filter(
                p => p.get("encounterId") !== encounterId
            );

            store.FlowsheetActions.loadFlowsheets(encounterId, response.PatientId);
        }
    }

    private DeleteMedicalEncounter(response: ServerEvent, store: PatientStore): void {
        let activePatientId = response.PatientId;
        let noteModel = store.PatientMedicalNoteModels[activePatientId];
        let summaryModel = store.PatientMedicalSummaryModels[activePatientId];
        let hasPermanentlyBlockingElements = response.Data.PermanentBlockingDeleteItems &&
            response.Data.PermanentBlockingDeleteItems.length > 0;
        let hasTemporarilyBlockingElements = response.Data.TemporaryBlockingDeleteItems &&
            response.Data.TemporaryBlockingDeleteItems.length > 0;
        noteModel = noteModel.set("PermanentBlockingDeleteItems", null);
        noteModel = noteModel.set("TemporaryBlockingDeleteItems", null);

        noteModel = noteModel.set("ShowDeleteNoteWarningPopup", false);
        sessionStore.hideGlobalOverlay();

        if (response.Data.Success == true) {
            store.ActionsMedicalSummary.LoadPatientCumulativeProfile(activePatientId, null, true, true, "PatientMedicalNoteResponseHandler_DeleteMedicalNote", null, true);
            store.ActionsProfile.loadPatient(activePatientId);
            this.updateSecondScreenModel(response.PatientId, response.Data.EncounterId, true);
        }

        else if (response.Data.Success == false && !hasPermanentlyBlockingElements && !hasTemporarilyBlockingElements)
            noteModel = noteModel.set("MedicalNoteErrorWarningPopup", true);

        else if (response.Data.Success == false && hasPermanentlyBlockingElements) {
            noteModel = noteModel.set("MedicalNoteErrorWarningPopup", true);
            noteModel = noteModel.set("PermanentBlockingDeleteItems", response.Data.PermanentBlockingDeleteItems);
        }

        else if (response.Data.Success == false && hasTemporarilyBlockingElements) {
            noteModel = noteModel.set("MedicalNoteErrorWarningPopup", true);
            noteModel = noteModel.set("TemporaryBlockingDeleteItems", response.Data.TemporaryBlockingDeleteItems);
        }

        store.PatientMedicalNoteModels[response.PatientId] = noteModel;
        store.PatientMedicalSummaryModels[response.PatientId] = summaryModel;
    }

    // TODO - remove copy-pasta, merge facturation net into provider interfaces
    // code is mostly duplicated in PortfolioResponseHandler.ts
    private async updateInvoiceStatuses(response: ServerEvent, store: PatientStore): Promise<void> {
        const resolvedProvider = sessionStore.getCurrentEmployee().getBillingProviderResolved();

        if (BillingProviderHelper.shouldUseLegacyMedfarBilling(resolvedProvider)) {
            return;
        }

        if (BillingProviderHelper.shouldUseLegacyFacturationNetBilling(resolvedProvider)) {
            this.updateFacturationNetInvoiceStatuses(response, store);
            return;
        }

        await this.updateExternalBillingClaimsForSingleEncounterAsync(response, store);
    }

    private updateFacturationNetInvoiceStatuses(response: ServerEvent, store: PatientStore): void {
        if (sessionStore.getCurrentEmployee().getBillingProviderResolved() != BillingProvider.FacNet)
            return;

        let noteModel = store.PatientMedicalNoteModels[response.PatientId];

        // get invoice IDs to update
        let invoiceIds = noteModel.get("FacturationNetBills")
            .filter(bill => !FacturationNetManager.isInvoiceStatusClosed() && bill.CreatedByEmployeeId === sessionStore.getCurrentEmployee().ContactFacetId)
            .map(bill => parseInt(bill.ClaimId))
            .toArray();

        if (!invoiceIds.length)
            return;

        // fetch updated invoices
        FacturationNetManager.getUpdatedClaimStatuses(
            invoiceIds,
            (updatedClaims, deletedIds, errors) => {
                if (errors && errors.length)
                    return;

                let updatedClaimsLookup = updatedClaims.toDictionary(x => x.ClaimId);

                let noteModel = store.PatientMedicalNoteModels[response.PatientId];

                let claims = noteModel.get("FacturationNetBills")
                    .filter(x => !deletedIds.contains(x.ClaimId))
                    .toArray();
                for (let claim of claims) {
                    let updatedClaim = updatedClaimsLookup[claim.ClaimId];
                    if (updatedClaim) {
                        claim.Status = updatedClaim.ClaimStatus;
                        claim.Codes = updatedClaim.Codes;
                        claim.PreAmount = updatedClaim.PreAmount;
                        claim.Amount = updatedClaim.Amount;
                    }
                }

                noteModel = noteModel.set("FacturationNetBills", claims.toImmutable());
                store.PatientMedicalNoteModels[response.PatientId] = noteModel;
                store.EmitChangeToListeners(); // because this is in response of a server call that did not go through actions
            }
        );
    }

    private async updateExternalBillingClaimsForSingleEncounterAsync(
        response: ServerEvent,
        store: PatientStore
    ): Promise<void> {
        const encounterId = store.PatientMedicalNoteModels[response.PatientId].get("Encounter").Id;

        sessionStore.dispatch(
            LocalPatientOperations.MedicalNote.ExternalBilling_ReloadClaims,
            {
                encounterId: encounterId,
                forceStatusUpdate: false
            }
        );
    }

    private loadAttachedCommunications(response: ServerEventWithThen<any, () => void>, store: PatientStore): void {
        if (response.Data.Success) {
            let noteModel = store.PatientMedicalNoteModels[response.PatientId];

            noteModel = noteModel
                .set("IndividualTasksLinkedItems", response.Data.IndividualTaskLinkedItems)
                .set("AreAllTasksCompleted", response.Data.AreAllTasksCompleted)
                .set("IndividualMessageLinkedItems", response.Data.IndividualMessageLinkedItems)
                .set("AreAllMessagesRead", response.Data.AreAllMessagesRead)
                .set("IndividualRecallLinkedItems", response.Data.IndividualRecallLinkedItems)
                .set("AreAllRecallsCompleted", response.Data.AreAllRecallsCompleted);

            store.PatientMedicalNoteModels[response.PatientId] = noteModel;
        }
    }

    private finishRequestsPortalSync(response: ServerEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[response.PatientId];
        let portalSyncModel = noteModel.get("PortalLinkedItemModel");

        if (response.hasErrors()) {
            portalSyncModel = portalSyncModel.set("ErrorMessage", response.Errors[0].Message);
            noteModel = noteModel.set("PortalLinkedItemModel", portalSyncModel);

            store.PatientMedicalNoteModels[response.PatientId] = noteModel;
            return;
        }

        const objectId = response.Data["ObjectId"];
        const sharedItem = response.Data["SharedItem"];
        const sharedItemMessages = response.Data["Messages"];

        switch (noteModel.get("PortalSyncPopupType")) {
            case MyleObjectType.ExamRequest:
                noteModel.set(
                    "Requests",
                    noteModel.get("Requests").mutate(
                        request => request.updateShareableModel(sharedItem, sharedItemMessages),
                        x => x.RequestId === objectId
                    )
                );
                break;

            case MyleObjectType.ConsultationRequest:
                noteModel.set(
                    "ConsultationRequests",
                    noteModel.get("ConsultationRequests").mutate(
                        request => request.updateShareableModel(sharedItem, sharedItemMessages),
                        x => x.ConsultationRequestId === objectId
                    )
                );
                break;
        }

        portalSyncModel = portalSyncModel
            .set("SharedItem", sharedItem)
            .set("LinkedItemMessages", sharedItemMessages.toImmutable());

        noteModel = noteModel.set("IsRequestPortalPopupOpen", false);
        sessionStore.hideGlobalOverlay();

        portalSyncModel = portalSyncModel.set("IsSending", false);
        noteModel = noteModel.set("PortalLinkedItemModel", portalSyncModel);

        store.PatientMedicalNoteModels[response.PatientId] = noteModel;
    }

    private reloadSharedItem(response: ServerEventWithThen<any, (sharedItem: SharedItem) => void>): void {
        const sharedItem = response.Data.SharedItem;

        response.then && response.then(sharedItem);
    }

    private finishPortalSync(response: ServerEventWithThen<any, () => void>, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[response.PatientId];
        let portalSyncModel = noteModel.get("PortalLinkedItemModel");

        if (response.hasErrors()) {
            portalSyncModel = portalSyncModel.set("ErrorMessage", response.Errors[0].Message);
            noteModel = noteModel.set("PortalLinkedItemModel", portalSyncModel);
            store.PatientMedicalNoteModels[response.PatientId] = noteModel;
            return;
        }

        const objectId = response.Data["ObjectId"];
        const sharedItem = response.Data["SharedItem"];
        const sharedItemMessages = response.Data["Messages"];

        switch (noteModel.get("PortalSyncPopupType")) {
            case MyleObjectType.ExamRequest:
                noteModel.set(
                    "Requests",
                    noteModel.get("Requests").mutate(
                        request => request.updateShareableModel(sharedItem, sharedItemMessages),
                        x => x.RequestId === objectId
                    )
                );
                break;

            case MyleObjectType.ConsultationRequest:
                noteModel.set(
                    "ConsultationRequests",
                    noteModel.get("ConsultationRequests").mutate(
                        request => request.updateShareableModel(sharedItem, sharedItemMessages),
                        x => x.ConsultationRequestId === objectId
                    )
                );
                break;

            case MyleObjectType.MedicalNoteForm:
                noteModel.set(
                    "PatientForms",
                    noteModel.get("PatientForms").mutate(
                        request => request.updateShareableModel(sharedItem, sharedItemMessages),
                        x => x.Id === objectId
                    )
                );

                patientStore.ActionsMedicalNote.reloadEncounterForms(noteModel.get("Encounter").Id, noteModel.get("Encounter").PatientId);
                break;

            case MyleObjectType.ObstetricalFile:
                const file = noteModel.get("ObstetricalFileModel");
                file.updateShareableModel(sharedItem, sharedItemMessages);
                noteModel.set("ObstetricalFileModel", file);
                break;

            case MyleObjectType.WorkSafeBc:
                noteModel.set(
                    "WorkSafeBcForms",
                    noteModel.get("WorkSafeBcForms").mutate(
                        form => form.updateShareableModel(sharedItem, sharedItemMessages),
                        x => x.Id === objectId
                    )
                );
                break;
            default:
                integrationBus.emit(new PortalSyncSuccessEvent({
                    portalSyncPopupType: noteModel.get("PortalSyncPopupType"),
                    objectId,
                    encounterId: noteModel.get("Encounter").Id,
                    sharedItem: sharedItem,
                    sharedItemMessages: sharedItemMessages
                }));
        }

        portalSyncModel = portalSyncModel
            .set("SharedItem", sharedItem)
            .set("LinkedItemMessages", sharedItemMessages.toImmutable());

        noteModel = noteModel
            .set("IsRequestPortalPopupOpen", false)
            .set("UnsignedPortalFileName", sharedItem)
            .set("UnsignedPortalFileUrl", sharedItem)
            .set("PortalFileName", sharedItem)
            .set("PortalFileUrl", sharedItem);

        sessionStore.hideGlobalOverlay();

        portalSyncModel = portalSyncModel.set("IsSending", false);
        noteModel = noteModel.set("PortalLinkedItemModel", portalSyncModel);

        store.PatientMedicalNoteModels[response.PatientId] = noteModel;

        response.then && response.then();
    }

    private reloadForms(response: ServerEvent, store: PatientStore): void {
        const responseData = response.Data;
        let patientNote = store.PatientMedicalNoteModels[response.PatientId];
        if (patientNote &&
            patientNote.get("Encounter").Id == responseData.EncounterId &&
            responseData.PatientForms) {
            const patientForms = responseData.PatientForms;
            patientForms.forEach(f => { f.contextOpen = false; }); // new property for front end use
            patientNote = patientNote
                .set(
                    "PatientForms",
                    Immutable.List(patientForms.map(form => PdfFormInboxItem.fromPatientForms(form, responseData.EncounterId)))
                )
                .set("IsPatientFormStatusStale", false);

            store.PatientMedicalNoteModels[response.PatientId] = patientNote;
        }
    }

    private handleOnObstetricalContributionCreated(response: ServerEvent, store: PatientStore): void {
        const responseData = response.Data;
        const patientId = response.PatientId;

        let patientNote = store.PatientMedicalNoteModels[patientId];
        if (patientNote) {
            const obstetricalFileModel = new ObstetricalFileModel(responseData.ObstetricalFileModel);
            patientNote = patientNote
                .set("ObstetricalFileModel", obstetricalFileModel)
                .set("ObstetricalFileFormDisplay", FormDisplay.prepareForObstetricalFile(obstetricalFileModel).extend());

            if (!obstetricalFileModel.IsNewObstetricalFile)
                patientNote = patientNote.set("PreviousObstetricalFiles", responseData.PreviousObstetricalFiles?.map(p => new BaseObstetricalFileModel(p)) ?? []);

            store.PatientMedicalNoteModels[patientId] = patientNote;

            store.updateContributorsEncountersData(patientId, patientNote.get("Encounter").Id);
        }

        store.ActionsMedicalSummary.loadObstetricalSummary(store.getCurrentPatient().PatientResponse.ContactFacet_id);
    }

    private deleteObstetricalFile(response: ServerEvent, store: PatientStore): void {
        if (response.hasErrors())
            return;

        const responseData = response.Data;
        let patientNote = store.PatientMedicalNoteModels[response.PatientId];

        if (patientNote) {
            patientNote = patientNote
                .set("ObstetricalFileModel", null)
                .set("PreviousObstetricalFiles", responseData.PreviousObstetricalFiles?.map(p => new ObstetricalFileModel(p)) ?? []);

            store.PatientMedicalNoteModels[response.PatientId] = patientNote;

            store.ActionsMedicalItems.LoadVitalSigns(response.PatientId);
            store.ActionsMedicalSummary.loadObstetricalSummary(store.getCurrentPatient().PatientResponse.ContactFacet_id);
            store.updateContributorsEncountersData(response.PatientId, patientNote.get("Encounter").Id);
        }
    }

    private loadWorkSafeBcForms(response: ServerEvent, store: PatientStore): void {
        if (response.hasErrors())
            return;

        const workerForms: WorkSafeBcFormLight[] = response.Data.Forms?.map(p => new WorkSafeBcFormLight(p)) ?? [];

        // update note model
        store.PatientMedicalNoteModels[response.PatientId] = store.PatientMedicalNoteModels[response.PatientId]
            .set("WorkSafeBcForms", workerForms);
    }

    private loadSentFaxesByItemIds(response: ServerEvent, store: PatientStore): void {
        const responseData = response.Data;

        if (!responseData.HasError) {
            const patientId = store.getCurrentPatientContactId();
            const sentFaxes = store.PatientMedicalNoteModels[patientId].get("SentFaxes") ?? {};

            for (const itemId of Object.keys(responseData.SentFaxes || {})) {
                const sentFax = responseData.SentFaxes[itemId];

                if (sentFax.any())
                    sentFaxes[itemId] = sentFax;
                else
                    delete sentFaxes[itemId];
            }

            store.PatientMedicalNoteModels[patientId] = store.PatientMedicalNoteModels[patientId].set("SentFaxes", sentFaxes);
        }
    }

    private loadSignedPrintAuthorizations(response: ServerEvent, store: PatientStore): void {
        const responseData = response.Data;
        const prescriptionSignatures: SignedPrintAuthorization[] = responseData.PrescriptionSignatures;
        const consultationRequestSignatures: SignedPrintAuthorization[] = responseData.ConsultationRequestSignatures;
        const requestSignatures: SignedPrintAuthorization[] = responseData.RequestSignatures;
        const patientFormSignatures: SignedPrintAuthorization[] = responseData.PatientFormSignatures;

        if (!prescriptionSignatures && !consultationRequestSignatures && !requestSignatures && !patientFormSignatures)
            return;

        const summary = store.PatientMedicalSummaryModels[response.PatientId];
        if (!summary)
            return;

        const signedPrintAuthorizationsByPrescriptionId = prescriptionSignatures?.toLookup(p => p.PrescriptionId);
        const signedPrintAuthorizationsByConsultationRequestId = consultationRequestSignatures?.toLookup(p => p.ConsultationRequestId);
        const signedPrintAuthorizationsByRequestId = requestSignatures?.toLookup(p => p.RequestId);
        const signedPrintAuthorizationsByPatientFormId = patientFormSignatures?.toLookup(p => p.PatientFormId);

        // prescriptions
        if (signedPrintAuthorizationsByPrescriptionId) {
            const prescriptions = summary.get("Prescriptions")
                .mutate(s => s.SignedPrintAuthorizations = null)
                .toArray()
                .toDictionary(p => p.PrescriptionId);

            for (const prescriptionId of Object.keys(signedPrintAuthorizationsByPrescriptionId)) {
                if (prescriptions[prescriptionId])
                    prescriptions[prescriptionId].SignedPrintAuthorizations = signedPrintAuthorizationsByPrescriptionId[prescriptionId];
            }

            store.PatientMedicalSummaryModels[responseData.PatientContactId] = summary.set(
                "Prescriptions",
                Object.keys(prescriptions)
                    .map(p => prescriptions[p])
                    .toImmutable()
            );
        }

        let noteModel = store.PatientMedicalNoteModels[response.PatientId];

        // consultation requests
        if (signedPrintAuthorizationsByConsultationRequestId) {
            const consultationRequests = noteModel
                .get("ConsultationRequests")
                .mutate(s => s.SignedPrintAuthorizations = null)
                .toArray()
                .toDictionary(p => p.ConsultationRequestId);

            for (const consultationRequestId of Object.keys(signedPrintAuthorizationsByConsultationRequestId)) {
                if (consultationRequests[consultationRequestId])
                    consultationRequests[consultationRequestId].SignedPrintAuthorizations = signedPrintAuthorizationsByConsultationRequestId[consultationRequestId];
            }

            noteModel = noteModel.set(
                "ConsultationRequests",
                Object.keys(consultationRequests)
                    .map(p => consultationRequests[p])
                    .toImmutable()
            );
        }

        // exam requests
        if (signedPrintAuthorizationsByRequestId) {
            let examRequests = noteModel.get("Requests")
                .mutate(s => s.SignedPrintAuthorizations = null)
                .toArray()
                .toDictionary(p => p.RequestId);

            for (const examRequestId of Object.keys(signedPrintAuthorizationsByRequestId)) {
                if (examRequests[examRequestId])
                    examRequests[examRequestId].SignedPrintAuthorizations = signedPrintAuthorizationsByRequestId[examRequestId];
            }

            noteModel = noteModel.set(
                "Requests",
                Object.keys(examRequests)
                    .map(p => examRequests[p])
                    .toImmutable()
            );
        }

        // patient forms
        if (signedPrintAuthorizationsByPatientFormId) {
            let patientForms = noteModel.get("PatientForms")
                .mutate(s => s.SignedPrintAuthorizations = null)
                .toArray()
                .toDictionary(p => p.Id);

            for (const patientFormIds of Object.keys(signedPrintAuthorizationsByPatientFormId)) {
                if (patientForms[patientFormIds])
                    patientForms[patientFormIds].SignedPrintAuthorizations = signedPrintAuthorizationsByPatientFormId[patientFormIds].filter(p => p.EncounterId === noteModel.get("Encounter").Id);
            }

            noteModel = noteModel.set(
                "PatientForms",
                Object.keys(patientForms)
                    .map(p => patientForms[p])
                    .toImmutable()
            );
        }

        store.PatientMedicalNoteModels[response.PatientId] = noteModel;
    }

    private updateSecondScreenModel(patientId: Guid, encounterId: string, isEncounterDeleted: boolean = false): void {
        if (SecondScreenNoteService.getInstance().isChildWindowOpen())
            SecondScreenNoteService.getInstance().updateModel(new SecondScreenNoteData({
                patientId: patientId,
                encounterId: encounterId,
                isEncounterDeleted: isEncounterDeleted
            }));
    }
}

interface IMyle_Patient_MedicalNote_GetStatusRequest {
    EncounterId: string,
    PatientContactId: string,
    callerIdentifier: string
}

interface IMyle_Patient_MedicalNote_GetFormPersistRequest {
    EncounterId: Guid,
    PatientFormId: string,
    IsPersistant: boolean
}

interface IMyle_Patient_MedicalNote_GetUpdateConsultationRequest {
    EncounterId: Guid,
    ConsultationDate: Date,
    ConsultationTypeLookupId: string,
    ConsultationContextLookupId: string,
    ConsultationSiteId: Guid,
    ConsultationDepartmentId: Guid,
    SetConsultationSiteAsActive: boolean,
    SetConsultationDepartmentAsActive: boolean,
    AppointmentId: string,
    EnableAppointmentLinkSuggestions: boolean
}

export {
    IMyle_Patient_MedicalNote_GetFormPersistRequest,
    IMyle_Patient_MedicalNote_GetStatusRequest,
    IMyle_Patient_MedicalNote_GetUpdateConsultationRequest,
    PatientMedicalNoteResponseHandler
};
